/**
 * TicketAnalysisPage.jsx
 * Admin-Ansicht: Ticket-Analyse über alle Agenten
 * Logik und Feldstruktur identisch mit AgentDetailTickets.
 */
import React, { useState, useMemo, useCallback, useEffect, useLayoutEffect, useRef } from 'react';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Legend } from 'recharts';
import { useApi } from './useApi';
// exportMonatsTrendExcel wird lazy importiert (exceljs ~280kB) — erst beim Klick.
import ZeitraumPicker from './ZeitraumPicker';
import LoadingBar from './LoadingBar';
import './AgentDetailView.css';
import './TicketAnalysisPage.css';

const API_BASE = process.env.REACT_APP_API_URL !== undefined ? process.env.REACT_APP_API_URL : 'http://localhost:3001';

// Minuten -> lesbare Geschaeftszeit-Dauer. z.B. 45 -> "45 min", 125 -> "2 h 5 min", 480 -> "8 h"
function formatDauer(min) {
  if (min == null || isNaN(min)) return '–';
  const m = Math.round(min);
  if (m < 60) return `${m} min`;
  const h = Math.floor(m / 60);
  const rest = m % 60;
  return rest === 0 ? `${h} h` : `${h} h ${rest} min`;
}

// Kompakte Variante fuer die Y-Achse: 45 -> "45m", 125 -> "2h", 480 -> "8h"
function formatDauerAchse(min) {
  if (min == null || isNaN(min)) return '';
  const m = Math.round(min);
  return m < 60 ? `${m}m` : `${Math.round(m / 60)}h`;
}

// ─── Konstanten (identisch mit AgentDetailTickets) ────────────────────────────

/**
 * Die Spalten des Monats-Trends — Reihenfolge, Beschriftung, Farbe.
 *
 * Eine Liste fuer alle vier Modi (Anzahl, Ø, Median, Langlieger). Der Modus
 * haengt nur ein Suffix an den Feldnamen; die Serverantwort traegt alle vier
 * Varianten je Spalte.
 *
 * „Basis Module" ergaenzt am 13.10.2026 (A7). Die Gruppe ist eine
 * Eingangs-Inbox ohne Mitglieder: der Voicebot legt das Ticket an, der KI-Bot
 * schlaegt eine Loesung vor, und je nach Modul wird geroutet. Deshalb eine
 * eigene Spalte statt in „Auftragsabwicklung" eingerechnet — sonst waere nicht
 * mehr erkennbar, wie viel ueber diesen Weg hereinkommt. Im Dropdown
 * „Mitarbeiter nach Abteilung" taucht sie bewusst NICHT auf, dort waere sie
 * leer.
 */
const TREND_SPALTEN = [
  { key: 'gesamt',             name: 'Alle Eingänge (First-Familie)', farbe: '#1e3a8a' },
  { key: 'auftragsabwicklung', name: 'Auftragsabwicklung',            farbe: '#3b82f6' },
  { key: 'buchhaltung',        name: 'Buchhaltung',                   farbe: '#16a34a' },
  { key: 'romandie',           name: 'Romandie',                      farbe: '#f59e0b' },
  { key: 'basisModule',        name: 'Basis Module (Inbox)',          farbe: '#ec4899' },
  { key: 'serviceDeskIT',      name: 'Service Desk IT',               farbe: '#14b8a6' },
  { key: 'secondLevel',        name: 'Second Level',                  farbe: '#a855f7' },
];

const STATUS_LABELS = {
  new: 'Neu', open: 'Offen', pending: 'Wartend',
  solved: 'Gelöst', closed: 'Geschlossen',
};
const STATUS_COLORS = {
  new: 'status--new', open: 'status--open', pending: 'status--pending',
  solved: 'status--solved', closed: 'status--closed',
};
const KANAL_LABELS = {
  mail: '✉ Mail', web: '🌐 Web', telefon: '📞 Telefon',
  automatisierung: '⚙ Auto', unbekannt: '–',
};

const EVENT_CONFIG = {
  created:     { color: '#185FA5', bg: '#E6F1FB', label: 'Erstellt',      icon: '●' },
  status:      { color: '#854F0B', bg: '#FAEEDA', label: 'Status',        icon: '◆' },
  assignee_id: { color: '#3B6D11', bg: '#EAF3DE', label: 'Übernahme',     icon: '▲' },
  group_id:    { color: '#993556', bg: '#FBEAF0', label: 'Weiterleitung', icon: '→' },
};

// ─── Hilfsfunktionen ──────────────────────────────────────────────────────────

// Datum als YYYY-MM-DD in LOKALER Zeit (kein toISOString → kein UTC-Versatz,
// der z.B. den 1. eines Monats in CEST auf den Vortag rutschen liesse).
function fmtLocalDate(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}
function todayStr()      { return fmtLocalDate(new Date()); }
function weekStartStr()  {
  const d = new Date(); const day = d.getDay() || 7;
  d.setDate(d.getDate() - day + 1); return fmtLocalDate(d);
}
function monthStartStr() {
  const d = new Date();
  return fmtLocalDate(new Date(d.getFullYear(), d.getMonth(), 1));
}
function fmtDateTime(iso) {
  if (!iso) return '–';
  return new Date(iso).toLocaleString('de-CH', {
    day: '2-digit', month: '2-digit', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
}

function avg(arr) {
  const v = (arr || []).filter(x => x != null && x > 0);
  return v.length ? Math.round(v.reduce((a, b) => a + b, 0) / v.length) : null;
}

// CSV-Export einer Ticket-Liste (BOM fuer Excel-UTF8). Modul-Helfer, wird pro
// Drilldown EINMAL im Header genutzt.
function exportTicketsCsv(tickets, csvName = 'tickets') {
  if (!tickets || tickets.length === 0) return;
  // KI-Spalten nur anhaengen, wenn der Drilldown sie ueberhaupt liefert —
  // sonst traegt jeder andere Export drei leere Spalten mit.
  const mitKi = tickets.some(t => t.kiUebernommen !== undefined);
  const headers = ['ID', 'Erstellt', 'Mitarbeiter', 'Betreff', 'Kanal', 'Status', 'Abteilung', 'Bearbeitungszeit (Min)', 'Solved', 'SolvedByAgent',
    ...(mitKi ? ['Reopens', 'Per Makro', 'KI-Training', 'Begründung KI nicht relevant'] : [])];
  const esc = (val) => {
    if (val == null) return '';
    const s = String(val);
    return (s.includes(',') || s.includes('\n') || s.includes('"')) ? `"${s.replace(/"/g, '""')}"` : s;
  };
  const rows = tickets.map(t => [
    t.id,
    t.created_at ? new Date(t.created_at).toLocaleString('de-CH') : '',
    t.agentName || '',
    t.subject || '',
    KANAL_LABELS[t.kanal] || t.kanal || '',
    STATUS_LABELS[t.status] || t.status || '',
    t.gruppeName || '',
    t.bearbeitungMin != null ? t.bearbeitungMin : '',
    t.solved_at ? new Date(t.solved_at).toLocaleString('de-CH') : '',
    t.solvedByAgent === true ? 'Ja' : t.solvedByAgent === false ? 'Nein' : '',
    // Vollstaendig, ungekuerzt — der Export ist der Ort, an dem die Texte
    // ausgewertet werden sollen (Zeilenumbrueche faengt esc() ab).
    ...(mitKi ? [
      t.ticketReopenGesamt ?? 0,
      t.kiUebernommen ? 'Ja' : 'Nein',
      t.kiTraining    ? 'Ja' : 'Nein',
      t.kiBegruendung || '',
    ] : []),
  ].map(esc).join(','));
  const csv = '﻿' + [headers.join(','), ...rows].join('\n');
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
  const url  = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `${csvName}_${new Date().toISOString().slice(0, 10)}.csv`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}


// CSV-Export fuer den Reopen-Drilldown — inkl. Reopen-spezifischer Spalten,
// damit sich die betroffenen Tickets auch ausserhalb des Dashboards
// identifizieren lassen (Excel-Auswertung).
function exportReopenedTicketsCsv(tickets, csvName = 'reopened-tickets') {
  if (!tickets || tickets.length === 0) return;
  const headers = [
    'ID', 'Erstellt', 'Mitarbeiter', 'Betreff', 'Kanal', 'Status', 'Abteilung',
    'Reopen-Anzahl bei diesem Mitarbeiter', 'Reopens Ticket gesamt', 'Klassifikation',
  ];
  const esc = (val) => {
    if (val == null) return '';
    const s = String(val);
    return (s.includes(',') || s.includes('\n') || s.includes('"')) ? `"${s.replace(/"/g, '""')}"` : s;
  };
  const rows = tickets.map(t => [
    t.id,
    t.created_at ? new Date(t.created_at).toLocaleString('de-CH') : '',
    t.agentName || '',
    t.subject || '',
    KANAL_LABELS[t.kanal] || t.kanal || '',
    STATUS_LABELS[t.status] || t.status || '',
    t.gruppeName || '',
    t.reopenAnzahlBeiAgent ?? '',
    t.ticketReopenGesamt ?? '',
    t.reopenKlasse === 'mehrfach' ? 'mehrfach wiedereröffnet' : t.reopenKlasse === 'einmal' ? 'einmal wiedereröffnet' : '',
  ].map(esc).join(','));
  const csv = '﻿' + [headers.join(','), ...rows].join('\n');
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
  const url  = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `${csvName}_${new Date().toISOString().slice(0, 10)}.csv`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

// CSV-Export fuer den Hold-Dauer-Drilldown — welche Tickets stecken hinter der
// Hold-Zeit eines Mitarbeiters in einem bestimmten Monat.
function exportHoldTicketsCsv(tickets, csvName = 'hold-tickets') {
  if (!tickets || tickets.length === 0) return;
  const headers = ['ID', 'Erstellt', 'Mitarbeiter', 'Betreff', 'Kanal', 'Status', 'Abteilung', 'Hold von', 'Hold bis', 'Hold-Dauer (Min)'];
  const esc = (val) => {
    if (val == null) return '';
    const s = String(val);
    return (s.includes(',') || s.includes('\n') || s.includes('"')) ? `"${s.replace(/"/g, '""')}"` : s;
  };
  const rows = tickets.map(t => [
    t.id,
    t.created_at ? new Date(t.created_at).toLocaleString('de-CH') : '',
    t.agentName || '',
    t.subject || '',
    KANAL_LABELS[t.kanal] || t.kanal || '',
    STATUS_LABELS[t.status] || t.status || '',
    t.gruppeName || '',
    t.holdVon ? new Date(t.holdVon).toLocaleString('de-CH') : '',
    t.holdBis ? new Date(t.holdBis).toLocaleString('de-CH') : '',
    t.holdMinuten ?? '',
  ].map(esc).join(','));
  const csv = '﻿' + [headers.join(','), ...rows].join('\n');
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
  const url  = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `${csvName}_${new Date().toISOString().slice(0, 10)}.csv`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

// CSV-Export der Forward-Journey-Liste (alle gefilterten Tickets, nicht nur die 50 sichtbaren)
function exportJourneyCsv(tickets, filterLabel) {
  if (!tickets || tickets.length === 0) return;
  const headers = [
    'ID', 'Erstellt', 'Betreff', 'Kanal',
    'Weitergeleitet nach', 'Aktuelle Gruppe', 'Mehr-Hops',
    'Status', 'Gelöst von', 'Gelöst bei Gruppe',
    'Bearbeitungszeit (Min)', 'Zendesk URL',
  ];
  const esc = (val) => {
    if (val == null) return '';
    const s = String(val);
    return (s.includes(',') || s.includes('\n') || s.includes('"')) ? `"${s.replace(/"/g, '""')}"` : s;
  };
  const rows = tickets.map(t => [
    t.id,
    t.createdAt ? new Date(t.createdAt).toLocaleString('de-CH') : '',
    t.subject || '',
    KANAL_LABELS[t.kanal] || t.kanal || '',
    t.forwardTarget?.name || '',
    t.currentGroup?.name || '',
    t.isMultiHop ? 'Ja' : 'Nein',
    STATUS_LABELS[t.status] || t.status || '',
    t.solvedByAgent?.name || '',
    t.solvedByAgent?.group?.name || '',
    t.bearbeitungMin != null ? t.bearbeitungMin : '',
    t.zendeskUrl || '',
  ].map(esc).join(','));
  const csv = '﻿' + [headers.join(','), ...rows].join('\n');
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
  const url  = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `forward-journey_${filterLabel}_${new Date().toISOString().slice(0, 10)}.csv`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

// ─── Timeline (identisch mit AgentDetailTickets) ──────────────────────────────

function TicketTimeline({ ticket, auditData, loading }) {
  if (loading) return (
    <div style={{ padding: '14px 16px', fontSize: 12, color: 'var(--color-text-secondary)' }}>
      Verlauf wird geladen…
    </div>
  );

  const history = auditData?.history || [];
  const events = [
    ticket.created_at
      ? { type: 'created', timestamp: ticket.created_at, kanal: ticket.kanal }
      : null,
    ...history.map(h => ({ ...h, type: h.field })),
  ].filter(Boolean).sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp));

  const groupEvents = history.filter(h => h.field === 'group_id');
  const routingPath = [];
  if (groupEvents.length > 0) {
    if (groupEvents[0].fromGroup) routingPath.push({ name: groupEvents[0].fromGroup, isCurrent: false });
    groupEvents.forEach((ev, i) => {
      if (ev.toGroup) routingPath.push({ name: ev.toGroup, isCurrent: i === groupEvents.length - 1 });
    });
  }

  return (
    <div style={{ padding: '12px 16px', background: 'var(--color-background-secondary)', borderBottom: '1px solid var(--color-border-tertiary)' }}>
      {/* Begründung „KI nicht relevant" — ungekürzt und zuoberst. BEWUSST
          nicht als Tabellenspalte: die Texte reichen bis 153 Zeichen und
          würden die Spaltenbreiten zerreissen. Hier stört die Länge nicht,
          und gelesen wird der Text ohnehin nur beim Nachgehen eines Falls. */}
      {ticket.kiBegruendung && (
        <div style={{ marginBottom: 12 }}>
          <div style={{ fontSize: 10, color: 'var(--color-text-secondary)', fontWeight: 500, marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Begründung: KI nicht relevant</div>
          <div style={{ fontSize: 13, lineHeight: 1.6, whiteSpace: 'pre-wrap' }}>{ticket.kiBegruendung}</div>
        </div>
      )}
      {routingPath.length > 1 && (
        <div style={{ marginBottom: 12 }}>
          <div style={{ fontSize: 10, color: 'var(--color-text-secondary)', fontWeight: 500, marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Routing-Pfad</div>
          <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 4 }}>
            {routingPath.map((step, i) => (
              <React.Fragment key={i}>
                <span style={{ padding: '3px 10px', borderRadius: 99, fontSize: 11, fontWeight: 500, background: step.isCurrent ? '#EAF3DE' : '#F1EFE8', color: step.isCurrent ? '#3B6D11' : '#5F5E5A', border: step.isCurrent ? '1px solid #97C459' : '0.5px solid rgba(0,0,0,0.15)' }}>{step.name}</span>
                {i < routingPath.length - 1 && <span style={{ color: 'var(--color-text-secondary)', fontSize: 12 }}>→</span>}
              </React.Fragment>
            ))}
          </div>
        </div>
      )}
      <div style={{ position: 'relative' }}>
        <div style={{ position: 'absolute', left: 14, top: 8, bottom: 8, width: 1, background: 'rgba(0,0,0,0.08)' }} />
        {events.length === 0 && (
          <div style={{ fontSize: 12, color: 'var(--color-text-secondary)', paddingLeft: 40 }}>Kein Verlauf verfügbar</div>
        )}
        {events.map((ev, i) => {
          const cfg = EVENT_CONFIG[ev.type] || EVENT_CONFIG.status;
          return (
            <div key={i} style={{ display: 'flex', gap: 12, marginBottom: 10, position: 'relative' }}>
              <div style={{ width: 28, height: 28, borderRadius: '50%', flexShrink: 0, background: cfg.bg, color: cfg.color, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 10, fontWeight: 700, zIndex: 1, border: `1px solid ${cfg.color}33` }}>{cfg.icon}</div>
              <div style={{ paddingTop: 4, flex: 1 }}>
                <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, flexWrap: 'wrap' }}>
                  <span style={{ fontSize: 10, fontWeight: 500, padding: '1px 6px', borderRadius: 99, background: cfg.bg, color: cfg.color }}>{cfg.label}</span>
                  <span style={{ fontSize: 11, color: 'var(--color-text-secondary)' }}>{fmtDateTime(ev.timestamp)}</span>
                </div>
                <div style={{ fontSize: 12, marginTop: 3, lineHeight: 1.5 }}>
                  {ev.type === 'created' && <>Ticket erstellt <span style={{ color: 'var(--color-text-secondary)' }}>— {ev.kanal === 'mail' ? 'E-Mail' : ev.kanal === 'web' ? 'Web' : ev.kanal === 'telefon' ? 'Telefon' : ev.kanal === 'automatisierung' ? 'Automatisierung' : 'Unbekannt'}</span></>}
                  {ev.type === 'status' && <>{ev.previousValue ? <><span style={{ color: 'var(--color-text-secondary)' }}>{STATUS_LABELS[ev.previousValue] || ev.previousValue}</span> → </> : ''}<strong style={{ fontWeight: 500 }}>{STATUS_LABELS[ev.value] || ev.value}</strong></>}
                  {ev.type === 'assignee_id' && <>Übernommen von <strong style={{ fontWeight: 500 }}>{ev.toAgent || '–'}</strong>{ev.fromAgent ? <span style={{ color: 'var(--color-text-secondary)' }}> (vorher: {ev.fromAgent})</span> : ''}</>}
                  {ev.type === 'group_id' && <>Weitergeleitet{ev.fromGroup ? <> von <strong style={{ fontWeight: 500 }}>{ev.fromGroup}</strong></> : ''} → <strong style={{ fontWeight: 500, color: '#993556' }}>{ev.toGroup || '–'}</strong></>}
                  {ev.byAgentName && ev.type !== 'assignee_id' && <span style={{ color: 'var(--color-text-secondary)', marginLeft: 6, fontSize: 11 }}>durch {ev.byAgentName}</span>}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ─── Einklappbarer + verschiebbarer Block ──────────────────────────────────────
// open/onToggle controlled von aussen (persistiert in localStorage).
// Drag&Drop ueber den Header — Reihenfolge via CSS-order (kein DOM-Umbau).
function CollapsibleSection({
  blockId, title, hint, children,
  open, onToggle, order,
  onDragStart, onDragOver, onDrop, isDragging,
}) {
  return (
    <div
      className="dashboard-block"
      style={{ marginBottom: '1.25rem', order, opacity: isDragging ? 0.4 : 1 }}
      onDragOver={(e) => { e.preventDefault(); }}
      onDrop={(e) => { e.preventDefault(); onDrop(blockId); }}
    >
      <div className="analysis-section-header collapsible-header">
        <span
          className="drag-handle"
          draggable
          onDragStart={() => onDragStart(blockId)}
          title="Zum Verschieben ziehen"
        >⠿</span>
        <h3 onClick={onToggle} style={{ cursor: 'pointer', margin: 0 }}>
          <span className="collapse-caret">{open ? '▾' : '▸'}</span> {title}
        </h3>
        {hint && <span className="hint">{hint}</span>}
      </div>
      {open && children}
    </div>
  );
}

// Default-Reihenfolge der verschiebbaren Bloecke (Kennzahlen ist FIX, nicht dabei)
const BLOCK_ORDER_DEFAULT = [
  'team', 'solver', 'fcrMonatlich', 'trend', 'produkt',
  'forwardZiele', 'reopen', 'kiBot', 'inbox', 'abgleich',
];
const LAYOUT_STORAGE_KEY = 'ticketAnalysis.layout.v2';

function loadLayout() {
  try {
    const raw = localStorage.getItem(LAYOUT_STORAGE_KEY);
    if (raw) {
      const p = JSON.parse(raw);
      const order = Array.isArray(p.order) ? p.order : BLOCK_ORDER_DEFAULT;
      // fehlende Block-IDs (neue Blocks) ans Ende anhaengen, unbekannte entfernen
      const merged = [...order.filter(id => BLOCK_ORDER_DEFAULT.includes(id))];
      BLOCK_ORDER_DEFAULT.forEach(id => { if (!merged.includes(id)) merged.push(id); });
      return { order: merged, open: p.open || {} };
    }
  } catch { /* ignore */ }
  return { order: [...BLOCK_ORDER_DEFAULT], open: {} };
}

// ─── Hauptseite ───────────────────────────────────────────────────────────────

// Initial Page-Size der Ticket-Liste — "Mehr laden"-Button erweitert
const PAGE_SIZE = 50;

// Zahlenzelle im KI-Block, die ihren eigenen Drilldown oeffnet. stopPropagation,
// damit der Klick nicht zusaetzlich den Zeilen-Drilldown aufklappt; bei 0 bleibt
// sie bewusst tot (ein leerer Drilldown waere nur irrefuehrend).
function KiZelle({ wert, aktiv, onKlick }) {
  return (
    <td
      onClick={(e) => { e.stopPropagation(); if (wert > 0) onKlick(); }}
      style={wert > 0 ? { cursor: 'pointer' } : undefined}
      className={aktiv ? 'is-selected' : ''}
    >
      {wert > 0 ? wert : <span className="col-muted">–</span>}
    </td>
  );
}

// ─── Eigenstaendige Ticket-Tabelle (Sortierung + Pagination + CSV + Timeline) ──
// Wird von jedem Drilldown-Block genutzt. expanded/onToggle/auditCache werden
// vom Parent geteilt (eine offene Timeline gleichzeitig, gemeinsamer Audit-Cache).
function TicketTable({ tickets, expanded, onToggle, auditCache, auditLoading }) {
  const [sortField, setSortField]       = useState('created_at');
  const [sortDir, setSortDir]           = useState('desc');
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);

  useEffect(() => { setVisibleCount(PAGE_SIZE); }, [tickets]);

  const sorted = useMemo(() => {
    const arr = [...tickets];
    arr.sort((a, b) => {
      let av = a[sortField], bv = b[sortField];
      if (typeof av === 'string') av = av.toLowerCase();
      if (typeof bv === 'string') bv = bv.toLowerCase();
      if (av < bv) return sortDir === 'asc' ? -1 : 1;
      if (av > bv) return sortDir === 'asc' ? 1 : -1;
      return 0;
    });
    return arr;
  }, [tickets, sortField, sortDir]);

  const handleSort = (field) => {
    if (sortField === field) setSortDir(d => (d === 'asc' ? 'desc' : 'asc'));
    else { setSortField(field); setSortDir('asc'); }
  };
  const SortIcon = ({ field }) => sortField !== field
    ? <span className="sort-icon sort-icon--inactive">↕</span>
    : <span className="sort-icon">{sortDir === 'asc' ? '↑' : '↓'}</span>;

  if (tickets.length === 0) return <div className="tickets-empty">Keine Tickets gefunden.</div>;

  // Reopen-Drilldown liefert zusaetzlich reopenKlasse ('einmal'/'mehrfach'),
  // Hold-Drilldown liefert holdMinuten — dann jeweils eigene Spalte +
  // farbliche Markierung zeigen, sonst normale Ansicht.
  const showReopen = tickets.some(t => t.reopenKlasse);
  const showHold   = tickets.some(t => t.holdMinuten != null);
  // KI-Drilldown liefert kiUebernommen — Stichwort `mailantwort_ki`. Das kommt
  // NUR aus dem Makro „Textvorlage::Mailantwort (KI)", nicht vom manuellen
  // Kopieren der Bot-Antwort; die Spalte heisst deshalb „per Makro" und nicht
  // „1:1" (Korrektur 17.09.2026).
  const showKi     = tickets.some(t => t.kiUebernommen !== undefined);
  // Trainings-Spalte nur, wo sie etwas zu sagen hat — bei „passende Lösung"
  // waere sie eine Spalte voller Striche.
  const showTrain  = tickets.some(t => t.kiTraining === true);
  // Bearbeitungszeit: in KI-Listen GAR NICHT (Fachentscheid 22.09.2026),
  // sonst nur, wenn ueberhaupt ein Wert vorhanden ist.
  //
  // Warum im KI-Block gar nicht: Die Zahl misst Geschaeftsminuten, in denen das
  // Ticket EINEM Agenten zugewiesen und zugleich offen war — und welcher Agent
  // das ist, entscheidet beim Zusammenfuehren die Reihenfolge der
  // Mitarbeiterliste (gemessen: 155 Tickets im Monat zeigen „–", obwohl eine
  // Zeit existiert, 68 die eines Unbeteiligten). Vor allem aber sagt sie ueber
  // den Bot nichts: Der schreibt seinen Vorschlag binnen Sekunden, die
  // Bearbeitungszeit beginnt erst danach und haengt an einem Menschen. Was im
  // KI-Block wirklich etwas aussagt — Reopen, Weiterleitungen, Zeit bis zur
  // Uebernahme — steht ohnehin da.
  //
  // In den uebrigen Listen bleibt sie: dort ist der Personenbezug gewollt.
  const showBearb = !showKi && tickets.some(t => typeof t.bearbeitungMin === 'number');
  const colSpan = 7 + (showBearb ? 1 : 0) + (showReopen ? 1 : 0) + (showHold ? 1 : 0)
    + (showKi ? 1 : 0) + (showTrain ? 1 : 0) + 1;

  return (
    <>
      <div className="tickets-table-wrapper">
        <table className="tickets-table">
          <thead><tr>
            <th onClick={() => handleSort('id')}>ID <SortIcon field="id" /></th>
            <th onClick={() => handleSort('created_at')}>Erstellt <SortIcon field="created_at" /></th>
            <th onClick={() => handleSort('agentName')}>Mitarbeiter <SortIcon field="agentName" /></th>
            <th onClick={() => handleSort('subject')}>Betreff <SortIcon field="subject" /></th>
            <th onClick={() => handleSort('kanal')}>Kanal <SortIcon field="kanal" /></th>
            <th onClick={() => handleSort('status')}>Status <SortIcon field="status" /></th>
            <th onClick={() => handleSort('gruppeName')}>Abteilung <SortIcon field="gruppeName" /></th>
            {showBearb && <th onClick={() => handleSort('bearbeitungMin')}>Bearbeitungszeit <SortIcon field="bearbeitungMin" /></th>}
            {showKi && <th onClick={() => handleSort('kiUebernommen')}>per Makro <SortIcon field="kiUebernommen" /></th>}
            {showTrain && <th onClick={() => handleSort('kiTraining')}>KI-Training <SortIcon field="kiTraining" /></th>}
            {showReopen && <th onClick={() => handleSort('ticketReopenGesamt')}>Reopen <SortIcon field="ticketReopenGesamt" /></th>}
            {showHold && <th onClick={() => handleSort('holdMinuten')}>Hold-Dauer <SortIcon field="holdMinuten" /></th>}
            <th></th>
          </tr></thead>
          <tbody>
            {sorted.slice(0, visibleCount).map(ticket => {
              const reopenBg = ticket.reopenKlasse === 'mehrfach' ? 'rgba(217, 48, 37, 0.12)'
                              : ticket.reopenKlasse === 'einmal'  ? 'rgba(230, 158, 0, 0.12)'
                              : showHold && ticket.holdMinuten > 0 ? 'rgba(24, 95, 165, 0.10)'
                              : undefined;
              return (
              <React.Fragment key={ticket.id}>
                <tr
                  style={{
                    cursor: 'pointer',
                    background: expanded === ticket.id ? 'var(--color-background-secondary)' : reopenBg,
                  }}
                  onClick={() => onToggle(ticket.id)}
                >
                  <td>
                    <a href={`https://supportsorba.zendesk.com/agent/tickets/${ticket.id}`}
                       target="_blank" rel="noopener noreferrer" className="ticket-link"
                       onClick={e => e.stopPropagation()}>#{ticket.id}</a>
                  </td>
                  <td style={{ fontSize: 12, whiteSpace: 'nowrap' }}>{fmtDateTime(ticket.created_at)}</td>
                  <td style={{ fontSize: 12 }}>{ticket.agentName || '–'}</td>
                  <td className="td-subject" title={ticket.subject}>{ticket.subject}</td>
                  <td><span className={`kanal-badge kanal--${ticket.kanal}`}>{KANAL_LABELS[ticket.kanal] || ticket.kanal}</span></td>
                  <td><span className={`status-badge ${STATUS_COLORS[ticket.status] || ''}`}>{STATUS_LABELS[ticket.status] || ticket.status}</span></td>
                  <td style={{ fontSize: 11, color: 'var(--text-secondary, #666)' }}>{ticket.gruppeName || '–'}</td>
                  {showBearb && <td>{ticket.bearbeitungFormatiert || '–'}</td>}
                  {showKi && (
                    <td style={{ fontSize: 11, fontWeight: 600,
                      color: ticket.kiUebernommen ? '#8A5700' : 'var(--text-secondary, #6b7280)' }}>
                      {ticket.kiUebernommen ? 'ja' : '–'}
                    </td>
                  )}
                  {showTrain && (
                    <td style={{ fontSize: 11, fontWeight: 600,
                      color: ticket.kiTraining ? '#166534' : 'var(--text-secondary, #6b7280)' }}>
                      {ticket.kiTraining ? 'ja' : '–'}
                    </td>
                  )}
                  {showReopen && (
                    // Kein Fallback mehr auf 'einmal': der Re-Open-Quote-Drilldown
                    // enthaelt ausschliesslich wiedereroeffnete Tickets, dort war
                    // das harmlos. Der KI-Drilldown mischt beides — dort stand
                    // dadurch bei JEDEM Ticket "einmal" (Fund 16.09.2026).
                    <td style={{ fontSize: 11, fontWeight: 600,
                      color: ticket.reopenKlasse === 'mehrfach' ? '#B3261E'
                           : ticket.reopenKlasse === 'einmal'   ? '#8A5700'
                           : 'var(--text-secondary, #6b7280)' }}>
                      {ticket.reopenKlasse === 'mehrfach' ? `mehrfach (${ticket.ticketReopenGesamt}x)`
                       : ticket.reopenKlasse === 'einmal'  ? 'einmal'
                       : '–'}
                    </td>
                  )}
                  {showHold && (
                    <td style={{ fontSize: 11, fontWeight: 600, color: '#185FA5' }}>
                      {formatDauer(ticket.holdMinuten)}
                    </td>
                  )}
                  <td style={{ fontSize: 11, color: '#185FA5', fontWeight: 500, whiteSpace: 'nowrap' }}>
                    {expanded === ticket.id ? '▲ Schliessen' : '▼ Verlauf'}
                  </td>
                </tr>
                {expanded === ticket.id && (
                  <tr><td colSpan={colSpan} style={{ padding: 0 }}>
                    <TicketTimeline ticket={ticket} auditData={auditCache[ticket.id]} loading={!!auditLoading[ticket.id]} />
                  </td></tr>
                )}
              </React.Fragment>
              );
            })}
          </tbody>
        </table>
        {visibleCount < sorted.length && (
          <div className="load-more-row">
            <button type="button" className="load-more-btn"
              onClick={() => setVisibleCount(c => Math.min(c + PAGE_SIZE, sorted.length))}>
              Weitere {Math.min(PAGE_SIZE, sorted.length - visibleCount)} laden
              <span className="load-more-hint">({visibleCount} / {sorted.length})</span>
            </button>
            {sorted.length - visibleCount > PAGE_SIZE && (
              <button type="button" className="load-more-btn load-more-btn--all"
                onClick={() => setVisibleCount(sorted.length)}>
                Alle {sorted.length} laden
              </button>
            )}
          </div>
        )}
      </div>
    </>
  );
}

// ─── Drilldown-Block: einklappbar + schliessbar, erscheint pro aktivem Filter ──
// Holt die Tickets ON-DEMAND vom selben Endpoint, der auch die Zahl im Block
// erzeugt → garantierte Konsistenz mit der angeklickten Zeile.
//   url      = vollstaendige Fetch-URL
//   respKey  = Feld in der Antwort mit der Ticket-Liste ('items' | 'tickets')
//   agentName= optional: setzt fehlenden Assignee-Namen (Agent-Detail liefert ihn nicht)
function DrilldownBlock({ title, url, respKey, agentName, csvName, onClose }) {
  const [open, setOpen]               = useState(true);
  const [tickets, setTickets]         = useState(null);
  const [loading, setLoading]         = useState(true);
  const [error, setError]             = useState(null);
  // Eigene Timeline-States (self-contained pro Drilldown)
  const [expanded, setExpanded]       = useState(null);
  const [auditCache, setAuditCache]   = useState({});
  const [auditLoading, setAuditLoading] = useState({});

  // Scroll-Pin: hält die Viewport-Position des Block-Kopfs konstant, wenn der
  // Block beim Laden/Auf-Zuklappen seine Hoehe aendert → die Seite springt
  // nicht, Inhalt waechst nur darunter (Flexbox-order bricht das native
  // Scroll-Anchoring des Browsers, daher manuell).
  const rootRef    = useRef(null);
  const prevTopRef = useRef(null);
  prevTopRef.current = rootRef.current ? rootRef.current.getBoundingClientRect().top : null;
  useLayoutEffect(() => {
    if (rootRef.current && prevTopRef.current != null) {
      const delta = rootRef.current.getBoundingClientRect().top - prevTopRef.current;
      if (Math.abs(delta) > 0.5) window.scrollBy(0, delta);
    }
  }, [loading, open, tickets]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true); setError(null);
    fetch(url, { credentials: 'include' })
      .then(r => { if (!r.ok) throw new Error(`HTTP ${r.status}`); return r.json(); })
      .then(data => {
        if (cancelled) return;
        let list = Array.isArray(data?.[respKey]) ? data[respKey] : [];
        if (agentName) list = list.map(t => ({ ...t, agentName: t.agentName || agentName }));
        setTickets(list);
      })
      .catch(e => { if (!cancelled) setError(e.message); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [url, respKey, agentName]);

  const handleToggle = async (ticketId) => {
    if (expanded === ticketId) { setExpanded(null); return; }
    setExpanded(ticketId);
    if (!ticketId || auditCache[ticketId]) return;
    setAuditLoading(prev => ({ ...prev, [ticketId]: true }));
    try {
      const res  = await fetch(`${API_BASE}/api/tickets/${ticketId}/timeline`, { credentials: 'include' });
      const data = await res.json();
      setAuditCache(prev => ({ ...prev, [ticketId]: data }));
    } catch {
      setAuditCache(prev => ({ ...prev, [ticketId]: { history: [] } }));
    } finally {
      setAuditLoading(prev => ({ ...prev, [ticketId]: false }));
    }
  };

  return (
    <div className="drilldown-block" ref={rootRef}>
      <div className="analysis-section-header collapsible-header drilldown-header">
        <h3 onClick={() => setOpen(o => !o)} style={{ cursor: 'pointer', margin: 0 }}>
          <span className="collapse-caret">{open ? '▾' : '▸'}</span> {title}
          {tickets != null && <span className="drilldown-count">{tickets.length}</span>}
        </h3>
        <button
          type="button"
          className="export-btn drilldown-export"
          onClick={() => {
            // KI zuerst pruefen: KI-Listen tragen ebenfalls reopenKlasse, waeren
            // also im Reopen-Export gelandet — und der kennt weder die
            // Makro-/Trainings-Spalten noch die Begruendung. exportTicketsCsv
            // fuehrt die Reopen-Zahl fuer diesen Fall selbst mit.
            const exporter = tickets?.some(t => t.kiUebernommen !== undefined) ? exportTicketsCsv
                           : tickets?.some(t => t.reopenKlasse) ? exportReopenedTicketsCsv
                           : tickets?.some(t => t.holdMinuten != null) ? exportHoldTicketsCsv
                           : exportTicketsCsv;
            exporter(tickets, csvName);
          }}
          disabled={!tickets || tickets.length === 0}
          title="Diese Auswahl als CSV exportieren"
        >
          ⬇ Export CSV
        </button>
        <button type="button" className="drilldown-close" onClick={onClose} title="Drilldown schliessen">✕</button>
      </div>
      {open && (
        <div style={{ padding: '0 4px 4px' }}>
          {loading      ? <div className="tickets-loading" style={{ padding: '16px' }}>Tickets werden geladen…</div>
           : error      ? <div className="error-banner">⚠ {error}</div>
           : <TicketTable
               tickets={tickets || []}
               expanded={expanded}
               onToggle={handleToggle}
               auditCache={auditCache}
               auditLoading={auditLoading}
             />}
        </div>
      )}
    </div>
  );
}

// ─── Inbox-Warteschlange (Live, Zendesk-Call on-demand) ───────────────────────
/**
 * Abgleich gegen Zendesk — beantwortet die Frage "fehlt uns etwas?".
 *
 * Eine fehlende Zeile erzeugt keine Fehlermeldung; die Summe ist einfach
 * kleiner, und ohne Vergleichswert faellt das niemandem auf. Genau deshalb
 * blieb eine Luecke von rund 5% ueber Monate unbemerkt (Fund 23.09.2026) und
 * wurde nur gefunden, weil jemand zufaellig nach EINEM Ticket fragte.
 *
 * Die Spalte, auf die es ankommt, ist "unerklaert". Sie soll klein sein und
 * klein bleiben — springt sie, stimmt etwas nicht.
 */
function AbgleichBlock({ fromDate, toDate }) {
  const [data,    setData]    = useState(null);
  const [loading, setLoading] = useState(false);
  const [error,   setError]   = useState(null);

  useEffect(() => {
    let abgebrochen = false;
    setLoading(true); setError(null);
    const url = `${API_BASE}/api/ticket-analysis/abgleich`
      + `?from=${encodeURIComponent(fromDate)}&to=${encodeURIComponent(toDate + 'T23:59:59')}`;
    fetch(url, { credentials: 'include' })
      .then(r => { if (!r.ok) throw new Error(`HTTP ${r.status}`); return r.json(); })
      .then(d => { if (!abgebrochen) setData(d); })
      .catch(e => { if (!abgebrochen) setError(e.message); })
      .finally(() => { if (!abgebrochen) setLoading(false); });
    return () => { abgebrochen = true; };
  }, [fromDate, toDate]);

  if (loading && !data) return <div className="hint" style={{ padding: '8px 4px' }}>Wird geladen …</div>;
  if (error)            return <div className="hint" style={{ padding: '8px 4px' }}>Abgleich nicht verfügbar: {error}</div>;
  if (!data)            return null;

  // Schwelle fuer die Hervorhebung: bis 2% der Zendesk-Menge gilt als normales
  // Rauschen (Tickets wandern zwischen Gruppen, Zeitzonen-Raender). Darueber
  // lohnt ein Blick — die Zahl ist bewusst eine Faustregel, kein Grenzwert.
  const auffaellig = r => r.zendesk > 0 && Math.abs(r.unerklaert) / r.zendesk > 0.02;

  const zeile = (r, fett) => (
    <tr key={r.gruppeId || 'gesamt'} style={fett ? { fontWeight: 600 } : undefined}>
      <td>{r.gruppeName}</td>
      <td className="num">{r.zendesk}</td>
      <td className="num">{r.unsere}</td>
      <td className="num">{r.differenz}</td>
      <td className="num col-muted">{r.zusammengefasst}</td>
      <td className="num col-muted">{r.ohneBearbeiter}</td>
      <td className="num" style={auffaellig(r) ? { color: 'var(--danger, #c0392b)', fontWeight: 700 } : undefined}>
        {r.unerklaert}
      </td>
    </tr>
  );

  return (
    <div style={{ padding: '0 4px 4px' }}>
      <table className="analysis-table analysis-table--kompakt">
        <thead>
          <tr>
            <th>Gruppe</th>
            <th className="num">in Zendesk</th>
            <th className="num">in dieser Auswertung</th>
            <th className="num">Differenz</th>
            <th className="num">davon zusammengefasst</th>
            <th className="num">davon ohne Bearbeiter</th>
            <th className="num">unerklärt</th>
          </tr>
        </thead>
        <tbody>
          {data.proGruppe.map(r => zeile(r, false))}
          {zeile(data.gesamt, true)}
        </tbody>
      </table>
      <p className="hint" style={{ marginTop: 6 }}>
        {'Verglichen wird, was Zendesk im Zeitraum als erstellt meldet, mit dem, was diese Seite '
         + 'auswertet — jeweils für die aktuelle Gruppe des Tickets. Zusammengefasste Tickets und '
         + 'solche ohne Bearbeiter fehlen erwartungsgemäss. Was danach übrig bleibt, steht unter '
         + '„unerklärt" und sollte klein sein. Negative Werte heissen, dass wir mehr haben als Zendesk '
         + 'in dieser Kategorie meldet — meist Tickets, die die Gruppe inzwischen gewechselt haben.'}
      </p>
    </div>
  );
}

function InboxBlock() {
  const [groups,     setGroups]     = useState(null);
  const [selectedId, setSelectedId] = useState('');
  const [data,       setData]       = useState(null);
  const [loading,    setLoading]    = useState(false);
  const [error,      setError]      = useState(null);
  const [loadedAt,   setLoadedAt]   = useState(null);

  useEffect(() => {
    fetch(`${API_BASE}/api/stats/groups`, { credentials: 'include' })
      .then(r => r.json())
      .then(list => setGroups(Array.isArray(list) ? list.sort((a, b) => a.name.localeCompare(b.name)) : []))
      .catch(() => setGroups([]));
  }, []);

  const fetchInbox = useCallback((groupId, nocache = false) => {
    if (!groupId) return;
    setLoading(true); setError(null);
    const url = `${API_BASE}/api/ticket-analysis/inbox?groupId=${encodeURIComponent(groupId)}${nocache ? '&nocache=1' : ''}`;
    fetch(url, { credentials: 'include' })
      .then(r => { if (!r.ok) throw new Error(`HTTP ${r.status}`); return r.json(); })
      .then(d => { setData(d); setLoadedAt(new Date()); })
      .catch(e => setError(e.message))
      .finally(() => setLoading(false));
  }, []);

  const handleExportExcel = useCallback(async () => {
    if (!data?.items?.length) return;
    const groupName = (groups || []).find(g => String(g.id) === String(selectedId))?.name || selectedId;
    const { exportInboxExcel } = await import('./exportInboxExcel');
    await exportInboxExcel(data.items, groupName);
  }, [data, groups, selectedId]);

  useEffect(() => {
    if (!selectedId) { setData(null); setLoadedAt(null); return; }
    fetchInbox(selectedId);
  }, [selectedId, fetchInbox]);

  const now = Date.now();

  return (
    <div>
      {/* Selector-Leiste */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14, flexWrap: 'wrap' }}>
        <select
          value={selectedId}
          onChange={e => setSelectedId(e.target.value)}
          style={{ padding: '6px 10px', border: '1px solid var(--border)', borderRadius: 6, fontSize: 13, background: 'var(--bg-input)', color: 'var(--text-primary)', minWidth: 220 }}
        >
          <option value="">Inbox auswählen…</option>
          {(groups || []).map(g => <option key={g.id} value={g.id}>{g.name}</option>)}
        </select>
        {selectedId && (
          <button
            type="button"
            className="journey-filter-btn"
            onClick={() => fetchInbox(selectedId, true)}
            disabled={loading}
            title="Frischen Zendesk-Call machen (umgeht 2-Min-Cache)"
          >
            ↻ Neu laden
          </button>
        )}
        {data?.items?.length > 0 && (
          <button
            type="button"
            className="journey-filter-btn"
            onClick={handleExportExcel}
            title="Als Excel exportieren (TableStyleMedium9)"
          >
            ⬇ Excel
          </button>
        )}
        {data && (
          <span style={{ marginLeft: 'auto', fontSize: 12, color: 'var(--text-secondary)' }}>
            {data.total} Ticket{data.total !== 1 ? 's' : ''}
            {loadedAt ? ` · Stand ${loadedAt.toLocaleTimeString('de-CH', { hour: '2-digit', minute: '2-digit' })} Uhr` : ''}
          </span>
        )}
      </div>

      {/* Inhalt */}
      {!selectedId && (
        <div className="tickets-empty">Inbox auswählen um die aktuelle Warteschlange zu sehen.</div>
      )}
      {loading && (
        <div style={{ padding: '16px 4px', fontSize: 13, color: 'var(--text-secondary)' }}>Zendesk wird abgefragt…</div>
      )}
      {error && <div className="error-banner">⚠ {error}</div>}
      {data && !loading && data.items.length === 0 && (
        <div className="tickets-empty">✓ Keine offenen Tickets in dieser Inbox.</div>
      )}
      {data && !loading && data.items.length > 0 && (
        <div style={{ overflowX: 'auto' }}>
          <table className="analysis-table">
            <thead><tr>
              <th>ID</th>
              <th>Status</th>
              <th>KdNr.</th>
              <th>Organisation</th>
              <th>Betreff</th>
              <th>Wartezeit</th>
              <th>Zugewiesen an</th>
            </tr></thead>
            <tbody>{data.items.map(t => {
              const ageMs    = now - new Date(t.createdAt || 0).getTime();
              const ageDays  = Math.floor(ageMs / 86400000);
              const ageHours = Math.floor((ageMs % 86400000) / 3600000);
              const ageStr   = ageDays >= 1 ? `${ageDays}d ${ageHours}h` : `${ageHours}h`;
              const ageClass = ageDays >= 7 ? 'quote-badge--low' : ageDays >= 2 ? 'quote-badge--mid' : 'quote-badge--good';
              return (
                <tr key={t.id}>
                  <td>
                    {t.zendeskUrl
                      ? <a href={t.zendeskUrl} target="_blank" rel="noopener noreferrer" className="ticket-link">#{t.id}</a>
                      : `#${t.id}`}
                  </td>
                  <td><span className={`status-badge ${STATUS_COLORS[t.status] || ''}`}>{STATUS_LABELS[t.status] || t.status}</span></td>
                  <td style={{ fontSize: 12 }}>{t.kdnr}</td>
                  <td style={{ fontSize: 12 }} title={t.orgName}>{t.orgName}</td>
                  <td className="td-subject" title={t.subject}>{t.subject}</td>
                  <td><span className={`quote-badge ${ageClass}`}>{ageStr}</span></td>
                  <td style={{ fontSize: 12 }}>{t.assigneeName}</td>
                </tr>
              );
            })}</tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export default function TicketAnalysisPage() {
  // Zeitraum-State im gleichen Format wie AgentDetailView: string oder Object
  const [zeitraum,    setZeitraum]    = useState('heute');
  const [auditCache,  setAuditCache]  = useState({});
  const [auditLoading,setAuditLoading]= useState({});
  const [refreshKey,  setRefreshKey]  = useState(0);  // erzwingt Re-Fetch aller Endpoints
  // Aktive Drilldowns (pro angewandtem Filter ein einklappbarer Ticket-Block).
  // Jeder Eintrag: { key, type, value, label }. Mehrere gleichzeitig moeglich.
  const [drilldowns,  setDrilldowns]  = useState([]);
  // Vorgezogen (war weiter unten) — wird schon hier gebraucht, um "solver"/
  // "reopen" nur bei aufgeklappter Sektion zu laden (Lazy-Load, siehe unten).
  const [layout, setLayout] = useState(loadLayout);

  // Date-Range aus Zeitraum-Auswahl ableiten
  const { fromDate, toDate } = useMemo(() => {
    if (typeof zeitraum === 'string') {
      const today = todayStr();
      if (zeitraum === 'heute') return { fromDate: today, toDate: today };
      if (zeitraum === 'woche') return { fromDate: weekStartStr(), toDate: today };
      if (zeitraum === 'monat') return { fromDate: monthStartStr(), toDate: today };
      return { fromDate: today, toDate: today };
    }
    if (zeitraum && zeitraum.from && zeitraum.to) {
      return { fromDate: zeitraum.from, toDate: zeitraum.to };
    }
    return { fromDate: todayStr(), toDate: todayStr() };
  }, [zeitraum]);

  // Volle Liste laden ohne Serverfilter — Drilldowns nutzen eigene Endpoints.
  // Kein limit → Backend liefert alle Tickets (fuer korrekte KPI-Berechnung).
  // Drilldowns senden limit=1000 separat (gefilterte Teilmenge, ausreichend).
  const params = {
    from: fromDate,
    to:   toDate + 'T23:59:59',
  };

  // _r (refreshKey) erzwingt Re-Fetch wenn der Aktualisieren-Button geklickt wird
  const { data: ticketData, loading, error } = useApi('/api/ticket-analysis', { ...params, _r: refreshKey }, { poll: false });

  // Neue Endpoints fuer Team-Vergleich, Mitarbeiter-nach-Abteilung, Stufe 2 Analysen
  const teamParams = { from: fromDate, to: toDate + 'T23:59:59', _r: refreshKey };
  const { data: teamData, loading: teamLoading } = useApi('/api/ticket-analysis/teams', teamParams, { poll: false });
  const { data: kiBotData } = useApi('/api/ticket-analysis/ki-bot', teamParams, { poll: false });
  const { data: abteilungenData } = useApi('/api/ticket-analysis/abteilungen', {}, { poll: false });
  const [selectedAbteilung, setSelectedAbteilung] = useState('');
  useEffect(() => {
    if (!selectedAbteilung && abteilungenData?.abteilungen?.length > 0) {
      setSelectedAbteilung(abteilungenData.abteilungen[0].gruppeId);
    }
  }, [abteilungenData, selectedAbteilung]);
  // Eigener, unabhaengiger Abteilungs-Selektor fuer "Re-Open-Quote" (eigenes
  // Dropdown, nicht an "Mitarbeiter nach Abteilung" gekoppelt — gleiche
  // Default-Logik: erste Abteilung sobald abteilungenData geladen ist).
  const [selectedAbteilungReopen, setSelectedAbteilungReopen] = useState('');
  useEffect(() => {
    if (!selectedAbteilungReopen && abteilungenData?.abteilungen?.length > 0) {
      setSelectedAbteilungReopen(abteilungenData.abteilungen[0].gruppeId);
    }
  }, [abteilungenData, selectedAbteilungReopen]);
  // Mitarbeiter-Block nutzt den zeitraum-String (heute/woche/monat) damit getKPIs
  // pro Agent exakt die Agenten-Ansicht-Logik anwendet (Cache-Pfade identisch).
  const solverParams = (typeof zeitraum === 'string')
    ? { zeitraum, gruppeId: selectedAbteilung, _r: refreshKey }
    : { zeitraum: 'custom', von: zeitraum.from, bis: zeitraum.to, gruppeId: selectedAbteilung, _r: refreshKey };
  // Lazy-Load (Performance-Fix 2026-07-21): "Mitarbeiter nach Abteilung" und
  // "Re-Open-Quote" laden erst, wenn die Sektion tatsaechlich aufgeklappt ist
  // — vorher liefen beide Abfragen bei JEDER Zeitraum-Auswahl mit, auch wenn
  // der Block eingeklappt/nie angeschaut wurde.
  const solverOpen = !!layout.open.solver;
  const reopenOpen  = !!layout.open.reopen;
  const { data: solverData,  loading: solverLoading  } = useApi('/api/ticket-analysis/department-solver', solverParams, { poll: false, enabled: solverOpen });
  const { data: trendData,   loading: trendLoading  } = useApi('/api/ticket-analysis/trend', { _r: refreshKey }, { poll: false });
  const { data: productData, loading: productLoading } = useApi('/api/ticket-analysis/products', { ...teamParams, limit: 10 }, { poll: false });
  const { data: forwardData, loading: forwardLoading } = useApi('/api/ticket-analysis/forward-targets', teamParams, { poll: false });
  // /reopened braucht jetzt eine gruppeId (Abteilungs-Filter, siehe Backend-
  // Aenderung 2026-07-21) — eigener Abteilungs-Selektor (selectedAbteilungReopen),
  // unabhaengig von "Mitarbeiter nach Abteilung".
  const { data: reopenData,  loading: reopenLoading  } = useApi('/api/ticket-analysis/reopened', { ...teamParams, gruppeId: selectedAbteilungReopen }, { poll: false, enabled: reopenOpen });
  const { data: journeyData, loading: journeyLoading } = useApi('/api/ticket-analysis/forward-journey', teamParams, { poll: false });

  // Gesamt-Ladezustand ueber ALLE zeitraum-/filterabhaengigen Bloecke — treibt
  // den schlanken Top-Ladebalken + sperrt die Steuerelemente waehrend des
  // Ladens (siehe LoadingBar/.controls-busy). abteilungenData bewusst nicht
  // dabei (laedt einmalig, haengt nicht am Zeitraum-Filter).
  const anyFilterLoading = loading || teamLoading || solverLoading || trendLoading ||
    productLoading || forwardLoading || reopenLoading || journeyLoading;

  // FCR monatlich — eigener Gruppen-Selektor, unabhängig vom Zeitraum-Filter
  const [fcrGruppeId, setFcrGruppeId] = useState('');
  const [fcrSelectedMonth, setFcrSelectedMonth] = useState('');
  useEffect(() => {
    if (!fcrGruppeId && abteilungenData?.abteilungen?.length > 0) {
      setFcrGruppeId(abteilungenData.abteilungen[0].gruppeId);
    }
  }, [abteilungenData, fcrGruppeId]);
  const { data: fcrData } = useApi(
    fcrGruppeId ? '/api/ticket-analysis/fcr-monthly' : null,
    { groupId: fcrGruppeId, _r: refreshKey },
    { poll: false }
  );
  // Auto-select most recent month when data arrives or group changes
  useEffect(() => {
    if (fcrData?.months?.length > 0) {
      setFcrSelectedMonth(prev => {
        if (prev && fcrData.months.includes(prev)) return prev;
        return fcrData.months[fcrData.months.length - 1];
      });
    }
  }, [fcrData]);

  // Monats-Trend Anzeige-Modus: 'anzahl' (Eingaenge) | 'bearbeitung' (Ø-Min)
  const [trendMode, setTrendMode] = useState('anzahl');
  const trendChartRef = useRef(null);          // Wrapper um das Chart-SVG (fuer Excel-Export)
  const [trendExporting, setTrendExporting] = useState(false);

  // Sub-Filter fuer Forward-Journey: 'alle' | 'unsolved' | 'multihop'
  const [journeyFilter, setJourneyFilter]     = useState('alle');
  // Expandiertes Ticket in der Journey-Ticket-Liste
  const [journeyExpanded, setJourneyExpanded] = useState(null);
  // Gewähltes Ziel im Forward-Ziele Block (null = alle anzeigen)
  const [journeySelectedTarget, setJourneySelectedTarget] = useState(null);

  // Drilldowns + Journey-Filter zuruecksetzen + manuelles Aktualisieren
  const handleResetFilters = useCallback(() => {
    setJourneyFilter('alle');
    setDrilldowns([]);
  }, []);
  const handleRefresh = useCallback(() => setRefreshKey(k => k + 1), []);

  const hatAktiveFilter = drilldowns.length > 0;

  // ── Layout: Block-Reihenfolge + Einklapp-Zustand (localStorage) ──────────────
  // (layout/setLayout jetzt weiter oben deklariert, siehe Kommentar dort)
  const [draggingId, setDraggingId] = useState(null);

  useEffect(() => {
    try { localStorage.setItem(LAYOUT_STORAGE_KEY, JSON.stringify(layout)); }
    catch { /* ignore */ }
  }, [layout]);

  const isOpen = useCallback((id) => !!layout.open[id], [layout.open]);
  const orderIndex = useCallback((id) => {
    const i = layout.order.indexOf(id);
    return i === -1 ? 999 : i;
  }, [layout.order]);

  const handleDragStart = useCallback((id) => setDraggingId(id), []);
  const handleDrop = useCallback((targetId) => {
    setLayout(l => {
      const src = draggingId;
      if (!src || src === targetId) return l;
      const order = l.order.filter(x => x !== src);
      const idx = order.indexOf(targetId);
      order.splice(idx, 0, src);  // src vor target einfuegen
      return { ...l, order };
    });
    setDraggingId(null);
  }, [draggingId]);

  const resetLayout = useCallback(() => {
    setLayout({ order: [...BLOCK_ORDER_DEFAULT], open: {} });
  }, []);

  // Props-Bundle fuer jeden Block
  const blockProps = (id) => ({
    blockId:     id,
    order:       orderIndex(id),
    open:        isOpen(id),
    onToggle:    () => setLayout(l => ({ ...l, open: { ...l.open, [id]: !l.open[id] } })),
    onDragStart: handleDragStart,
    onDrop:      handleDrop,
    isDragging:  draggingId === id,
  });

  const tickets = useMemo(() => ticketData?.items || [], [ticketData]);

  // Zeitraum als Query-String fuer den Agent-Detail-Endpoint (wie Top-Solver).
  const zeitraumQuery = useMemo(() => (typeof zeitraum === 'string')
    ? `zeitraum=${zeitraum}`
    : `zeitraum=custom&von=${zeitraum.from}&bis=${zeitraum.to}`,
  [zeitraum]);

  // Descriptor um die Fetch-URL ergaenzen — jedes Drilldown holt seine Tickets
  // vom SELBEN Endpoint, der auch die Zahl im Block erzeugt (Konsistenz).
  const makeDrill = useCallback((d) => {
    const analysisBase = `${API_BASE}/api/ticket-analysis?from=${encodeURIComponent(fromDate)}&to=${encodeURIComponent(toDate + 'T23:59:59')}`;
    let url, respKey = 'items', agentName;
    switch (d.type) {
      case 'team':    url = `${analysisBase}&groupId=${encodeURIComponent(d.value)}`; break;
      case 'forward': url = `${analysisBase}&forwardTo=${encodeURIComponent(d.value)}`; break;
      case 'produkt': url = `${analysisBase}&produkt=${encodeURIComponent(d.value)}`; break;
      case 'solver':  url = `${API_BASE}/api/agent-detail/${d.agentId}/tickets?${zeitraumQuery}&selfView=true&limit=all`;
                      respKey = 'tickets'; agentName = d.value; break;
      case 'reopenTickets':
                      url = `${API_BASE}/api/ticket-analysis/reopened/tickets?from=${encodeURIComponent(fromDate)}&to=${encodeURIComponent(toDate + 'T23:59:59')}&agentId=${encodeURIComponent(d.agentId)}`;
                      respKey = 'tickets'; agentName = d.value; break;
      case 'kiBot':   url = `${API_BASE}/api/ticket-analysis/ki-bot/tickets?from=${encodeURIComponent(fromDate)}&to=${encodeURIComponent(toDate + 'T23:59:59')}&segment=${encodeURIComponent(d.value)}${d.nurReopened ? '&nurReopened=1' : ''}`;
                      respKey = 'tickets'; break;
      case 'holdTickets':
                      url = `${API_BASE}/api/ticket-analysis/fcr-monthly/hold-tickets?agentId=${encodeURIComponent(d.agentId)}&month=${encodeURIComponent(d.month)}`;
                      respKey = 'tickets'; agentName = d.value; break;
      default:        url = analysisBase;
    }
    return { ...d, url, respKey, agentName };
  }, [fromDate, toDate, zeitraumQuery]);

  // Drilldown togglen (Klick auf eine Filter-Zeile):
  //   - gleiche Zeile erneut → schliessen (toggle off)
  //   - andere Zeile im SELBEN Block → ersetzt die bisherige (nur EINE Auswahl
  //     pro Block); andere Bloecke bleiben unabhaengig offen.
  const toggleDrilldown = useCallback((desc) => {
    setDrilldowns(ds => {
      if (ds.some(d => d.key === desc.key)) return ds.filter(d => d.key !== desc.key);
      return [...ds.filter(d => d.source !== desc.source), desc];
    });
  }, []);
  const closeDrilldown = useCallback((key) => {
    setDrilldowns(ds => ds.filter(d => d.key !== key));
  }, []);
  const isDrill = useCallback((key) => drilldowns.some(d => d.key === key), [drilldowns]);

  // Excel-Export Monats-Trend (GL): Chart-Bild + Datentabellen. Client-seitig.
  const handleExportTrendExcel = useCallback(async () => {
    if (!trendData?.months?.length) return;
    setTrendExporting(true);
    try {
      const { exportMonatsTrendExcel } = await import('./exportMonatsTrendExcel');
      await exportMonatsTrendExcel({
        months:    trendData.months,
        year:      trendData.year,
        chartNode: trendChartRef.current || null,
        chartMode: trendMode,
      });
    } catch (e) {
      // eslint-disable-next-line no-alert
      alert('Excel-Export fehlgeschlagen: ' + (e?.message || e));
    } finally {
      setTrendExporting(false);
    }
  }, [trendData, trendMode]);

  // Gefilterte Journey-Liste (wird fuer Anzeige UND CSV-Export genutzt)
  const journeyFiltered = useMemo(() => {
    if (!journeyData?.tickets) return [];
    return journeyData.tickets.filter(t => {
      if (journeySelectedTarget && String(t.forwardTarget?.id) !== String(journeySelectedTarget)) return false;
      if (journeyFilter === 'unsolved') return t.isUnsolved;
      if (journeyFilter === 'multihop') return t.isMultiHop;
      return true;
    });
  }, [journeyData, journeyFilter, journeySelectedTarget]);

  // Journey-Drilldown — laedt Timeline-Audit fuer ein Ticket
  const handleJourneyToggle = useCallback(async (ticketId) => {
    if (journeyExpanded === ticketId) { setJourneyExpanded(null); return; }
    setJourneyExpanded(ticketId);
    if (!ticketId || auditCache[ticketId]) return;
    setAuditLoading(prev => ({ ...prev, [ticketId]: true }));
    try {
      const res  = await fetch(`${API_BASE}/api/tickets/${ticketId}/timeline`, { credentials: 'include' });
      const data = await res.json();
      setAuditCache(prev => ({ ...prev, [ticketId]: data }));
    } catch {
      setAuditCache(prev => ({ ...prev, [ticketId]: { history: [] } }));
    } finally {
      setAuditLoading(prev => ({ ...prev, [ticketId]: false }));
    }
  }, [journeyExpanded, auditCache]);

  // Drilldowns eines Quell-Blocks rendern — erscheinen direkt unter dessen Inhalt.
  const renderDrilldowns = (source) => drilldowns
    .filter(d => d.source === source)
    .map(d => (
      <DrilldownBlock
        key={d.key}
        title={d.label}
        url={d.url}
        respKey={d.respKey}
        agentName={d.agentName}
        csvName={d.key.replace(/[^a-z0-9]+/gi, '_')}
        onClose={() => closeDrilldown(d.key)}
      />
    ));

  // KPIs am Kopf — clientseitig aus der geladenen Ticket-Liste
  const solved   = tickets.filter(t => t.status === 'solved' || t.status === 'closed');
  const offen    = tickets.filter(t => t.status === 'open' || t.status === 'new').length;
  const weiterleitungen = tickets.filter(t => t.weiterleitungZielGruppe != null && t.weiterleitungZielGruppe !== '').length;
  // Ø Bearbeitungszeit nur ueber abgeschlossene Tickets (Business-Hours-Wert vom Worker)
  const avgProcess = avg(solved.map(t => t.bearbeitungMin));

  // Eingangskanal-Verteilung — wie viele Tickets kamen ueber welchen Kanal rein
  const kanalReihenfolge = ['telefon', 'mail', 'web', 'automatisierung', 'unbekannt'];
  const kanalCounts = tickets.reduce((acc, t) => {
    const k = t.kanal || 'unbekannt';
    acc[k] = (acc[k] || 0) + 1;
    return acc;
  }, {});

  const quote = tickets.length > 0 ? Math.round((solved.length / tickets.length) * 100) : 0;
  const wlRate = tickets.length > 0 ? Math.round((weiterleitungen / tickets.length) * 100) : 0;
  const quoteClass = quote >= 60 ? 'quote-badge--good' : quote >= 35 ? 'quote-badge--mid' : 'quote-badge--low';
  const wlRateClass = wlRate < 20 ? 'quote-badge--good' : wlRate < 40 ? 'quote-badge--mid' : 'quote-badge--low';


  // Nur beim INITIALEN Laden (ohne bestehende Daten) den Loading-Bildschirm zeigen.
  // Bei Filter-Wechsel bleiben die vorherigen Daten sichtbar bis die neuen ankommen
  // -> kein Spruenge auf Seitenanfang.
  if (loading && !ticketData) return <div className="tickets-loading">Daten werden geladen…</div>;
  if (error)                  return <div className="error-banner">⚠ {error}</div>;

  return (
    <div style={{ padding: '1.5rem' }}>

      <LoadingBar active={anyFilterLoading} />

      {/* ── Filter-Leiste ── */}
      <div className="analysis-filters">
        <div className={`analysis-filters-row${anyFilterLoading ? ' controls-busy' : ''}`}>
          <div className="filter-group">
            <label>Zeitraum</label>
            <ZeitraumPicker value={zeitraum} onChange={setZeitraum} includeMonat />
          </div>

          <div className="filter-group filter-group--right">
            <label>&nbsp;</label>
            <div className="filter-actions">
              <button
                type="button"
                className="filter-action-btn"
                onClick={handleResetFilters}
                disabled={!hatAktiveFilter}
                title="Drilldowns schliessen"
              >
                ✕ Drilldowns schliessen
              </button>
              <button
                type="button"
                className="filter-action-btn"
                onClick={handleRefresh}
                title="Daten neu laden"
                disabled={anyFilterLoading}
              >
                {anyFilterLoading ? '⏳ Lädt…' : '↻ Aktualisieren'}
              </button>
              <button
                type="button"
                className="filter-action-btn"
                onClick={resetLayout}
                title="Block-Reihenfolge und Einklapp-Zustand zuruecksetzen"
              >
                ↺ Layout zurücksetzen
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* ── Kennzahlen (FIX, immer sichtbar, nicht verschiebbar) ── */}
      <div className="kpi-grid" style={{ marginBottom: '1.25rem' }}>
        <div className="kpi-card">
          <span className="kpi-label">Tickets gesamt</span>
          <span className="kpi-value">{tickets.length}</span>
          <span className="kpi-sub">im Zeitraum</span>
        </div>

        <div className="kpi-card kpi-card--green">
          <span className="kpi-label">Abgeschlossen</span>
          <span className="kpi-value">{solved.length}</span>
          <span className="kpi-sub">
            <span className={`quote-badge ${quoteClass}`}>{quote}%</span> von {tickets.length}
          </span>
        </div>

        <div className="kpi-card kpi-card--amber">
          <span className="kpi-label">Offen</span>
          <span className="kpi-value">{offen}</span>
          <span className="kpi-sub">aktuell offen / neu</span>
        </div>

        <div className="kpi-card kpi-card--blue">
          <span className="kpi-label">Weiterleitungen</span>
          <span className="kpi-value">{weiterleitungen}</span>
          <span className="kpi-sub">
            <span className={`quote-badge ${wlRateClass}`}>{wlRate}%</span> aller Tickets
          </span>
        </div>

        <div className="kpi-card">
          <span className="kpi-label">∅ Bearbeitung</span>
          <span className="kpi-value">{avgProcess != null ? `${avgProcess}m` : '–'}</span>
          <span className="kpi-sub">Business Hours, nur abgeschlossen</span>
        </div>

        <div className="kpi-card">
          <span className="kpi-label">Eingangskanal</span>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 2, marginTop: 4 }}>
            {kanalReihenfolge
              .filter(k => kanalCounts[k] > 0)
              .map(k => {
                const anzahl = kanalCounts[k];
                const anteil = tickets.length > 0 ? Math.round((anzahl / tickets.length) * 100) : 0;
                return (
                  <span key={k} style={{ fontSize: 12, display: 'flex', justifyContent: 'space-between', gap: 8 }}>
                    <span>{KANAL_LABELS[k] || k}</span>
                    <span style={{ color: 'var(--color-text-secondary)' }}>{anzahl} <span className="col-muted">({anteil}%)</span></span>
                  </span>
                );
              })}
          </div>
        </div>

        {/* KI-Bot-Kachel. Die Abdeckung ist die einzige Zahl der ganzen
            KI-Auswertung, die auch Tickets kennt, bei denen der Bot NICHT zum
            Zug kam — alles andere im Block beschreibt nur die Tickets mit
            Vorschlag. Ein Ticket, bei dem der Webhook still scheitert, wäre
            sonst nirgends sichtbar.
            Nenner ist bewusst die berechtigte Menge (vier First-Gruppen, Mail
            oder Web) und nicht „alle Tickets": Second Level und sämtliche
            Telefon-Tickets kommen für den Trigger nie in Frage. */}
        {kiBotData?.abdeckung && (
          <div className="kpi-card">
            <span className="kpi-label">KI-Bot</span>
            {/* Aufteilung im Tooltip statt als eigene Zeilen: Sie erklaert die
                Zahl, wenn jemand nachfragt, drueckt aber keinem die Details
                auf, der nur den Gesamtwert sehen will. */}
            <span className="kpi-value"
              title={Object.entries(kiBotData.abdeckung.nachHerkunft || {})
                .map(([k, v]) => `${k}: ${v.mit} von ${v.mit + v.ohne} (${Math.round(v.mit / (v.mit + v.ohne) * 100)}%)`)
                .join('\n')}
            >
              {Math.round(kiBotData.abdeckung.quote * 100)}%
            </span>
            <span className="kpi-sub" title="Tickets, für die der Trigger gilt: eingegangen in einer der vier First-Gruppen, per Mail oder Web-Formular">
              {kiBotData.abdeckung.mitVorschlag} von {kiBotData.abdeckung.berechtigt} berechtigten mit Vorschlag
              {/* Ohne diesen Hinweis liest man Tage mit, an denen es den Bot
                  noch gar nicht gab — die Quote misst dann die Inbetriebnahme
                  statt den Betrieb. */}
              {kiBotData.abdeckung.abVorZeitraumStart && (
                <> · ab {new Date(kiBotData.abdeckung.ab).toLocaleDateString('de-CH')}</>
              )}
            </span>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 2, marginTop: 6 }}>
              <span style={{ fontSize: 12, display: 'flex', justifyContent: 'space-between', gap: 8 }}>
                <span>Antwort verwendet</span>
                <span style={{ color: 'var(--color-text-secondary)' }}>
                  {kiBotData.nachBewertung?.['passende Lösung']
                    ? `${Math.round(kiBotData.nachBewertung['passende Lösung'].tickets / kiBotData.ticketsGesamt * 100)}%`
                    : '–'}
                </span>
              </span>
              {/* Der Vergleich, der der Auswertung bisher fehlte: dieselbe
                  berechtigte Menge, einziger Unterschied ist der Vorschlag. */}
              <span style={{ fontSize: 12, display: 'flex', justifyContent: 'space-between', gap: 8 }}
                title="Wieder aufgegangene Tickets — mit Bot-Vorschlag gegen berechtigte Tickets ohne Vorschlag, gleicher Zeitraum">
                <span>Reopen mit / ohne</span>
                <span style={{ color: 'var(--color-text-secondary)' }}>
                  {kiBotData.abdeckung.reopenMit.quote !== null ? `${Math.round(kiBotData.abdeckung.reopenMit.quote * 100)}%` : '–'}
                  {' / '}
                  {kiBotData.abdeckung.reopenOhne.quote !== null ? `${Math.round(kiBotData.abdeckung.reopenOhne.quote * 100)}%` : '–'}
                </span>
              </span>
            </div>
          </div>
        )}
      </div>

      {/* ── Verschiebbare Block-Liste (CSS-order + Drag&Drop) ── */}
      <div className="analysis-blocks">

      {/* ── Team-Vergleich (Eingangs-Kohorte) ── */}
      {teamData?.teams?.length > 0 && (
        <CollapsibleSection
          {...blockProps('team')}
          title="Team-Vergleich — Eingangs-Kohorte"
          hint="Neu eingegangene Tickets pro Abteilung (erste Inbox) — wurden sie auch dort geloest? Klick filtert die Liste"
        >
          <div style={{ overflowX: 'auto' }}>
            <table className="analysis-table">
              <thead><tr>
                <th>Eingangs-Abteilung</th>
                <th>Neu eingegangen</th>
                <th>Selbst gelöst</th>
                <th>Quote</th>
                <th>Offen</th>
                <th>Wartend</th>
                <th>Weitergereicht</th>
                <th>∅ Bearbeitung</th>
              </tr></thead>
              <tbody>{teamData.teams.map(t => {
                const tQuoteClass = t.quote >= 70 ? 'quote-badge--good' : t.quote >= 40 ? 'quote-badge--mid' : 'quote-badge--low';
                const wrRate = t.gesamt > 0 ? Math.round((t.weitergereicht / t.gesamt) * 100) : 0;
                return (
                  <tr
                    key={t.gruppeId}
                    className={isDrill(`team:${t.gruppeId}`) ? 'is-selected' : ''}
                    onClick={() => toggleDrilldown(makeDrill({ key: `team:${t.gruppeId}`, source: 'team', type: 'team', value: String(t.gruppeId), label: `Team-Eingang: ${t.gruppeName}` }))}
                    title="Klick — Tickets dieser Eingangs-Abteilung aufklappen"
                  >
                    <td className="col-name">{t.gruppeName}</td>
                    <td>{t.gesamt}</td>
                    <td className="col-solved">{t.abgeschlossen}</td>
                    <td><span className={`quote-badge ${tQuoteClass}`}>{t.quote}%</span></td>
                    <td>{t.offen}</td>
                    <td>{t.wartend}</td>
                    <td>{t.weitergereicht} <span className="col-muted">({wrRate}%)</span></td>
                    <td>{t.bearbeitungAvgFormatiert}</td>
                  </tr>
                );
              })}</tbody>
            </table>
          </div>
          {renderDrilldowns('team')}
        </CollapsibleSection>
      )}

      {/* ── Mitarbeiter nach Abteilung ── */}
      {abteilungenData?.abteilungen?.length > 0 && (
        <CollapsibleSection
          {...blockProps('solver')}
          title="Mitarbeiter nach Abteilung"
          hint="Alle Mitarbeiter der gewählten Abteilung — Solver-Pivot, Business-Hours-Bearbeitung — Klick filtert die Liste"
        >
          <div className="solver-abteilung-picker">
            <label htmlFor="solver-abteilung-select">Abteilung</label>
            <select
              id="solver-abteilung-select"
              value={selectedAbteilung}
              onChange={e => setSelectedAbteilung(e.target.value)}
            >
              {abteilungenData.abteilungen.map(ab => (
                <option key={ab.gruppeId} value={ab.gruppeId}>{ab.gruppeName}</option>
              ))}
            </select>
          </div>

          {solverData?.summary && (
            <div className="kpi-grid" style={{ marginBottom: '1rem' }}>
              <div className="kpi-card">
                <span className="kpi-label">Mitarbeiter</span>
                <span className="kpi-value">{solverData.summary.anzahlMitarbeiter}</span>
              </div>
              <div className="kpi-card">
                <span className="kpi-label">Tickets gesamt</span>
                <span className="kpi-value">{solverData.summary.gesamt}</span>
                <span className="kpi-sub">Abteilung, im Zeitraum</span>
              </div>
              <div className="kpi-card">
                <span className="kpi-label">Abgeschlossen</span>
                <span className="kpi-value">{solverData.summary.abgeschlossen}</span>
              </div>
              <div className="kpi-card">
                <span className="kpi-label">Quote</span>
                <span className="kpi-value">
                  {solverData.summary.quote === null ? '–' : `${solverData.summary.quote}%`}
                </span>
              </div>
              <div className="kpi-card">
                <span className="kpi-label">∅ Bearbeitung</span>
                <span className="kpi-value">{solverData.summary.bearbeitungAvgFormatiert}</span>
                <span className="kpi-sub">gewichtet, Business Hours</span>
              </div>
            </div>
          )}

          {solverData?.agents?.length > 0 ? (
            <div className="analysis-grid-chart-table">
              <ResponsiveContainer width="100%" height={240}>
                <BarChart
                  data={solverData.agents.map(a => ({ name: (a.agentName || '').split(' ')[0], fullName: a.agentName, abgeschlossen: a.abgeschlossen, gesamt: a.gesamt }))}
                  margin={{ top: 0, right: 0, left: -20, bottom: 0 }}
                >
                  <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                  <YAxis tick={{ fontSize: 11 }} />
                  <Tooltip labelFormatter={l => solverData.agents.find(a => (a.agentName || '').startsWith(l))?.agentName || l} />
                  <Bar isAnimationActive={false} dataKey="abgeschlossen" name="Abgeschlossen"  fill="#16a34a" />
                  <Bar isAnimationActive={false} dataKey="gesamt"        name="Tickets gesamt" fill="#bfdbfe" />
                </BarChart>
              </ResponsiveContainer>
              <div style={{ overflowX: 'auto' }}>
                <table className="analysis-table">
                  <thead><tr>
                    <th>Mitarbeiter</th>
                    <th>Tickets gesamt</th>
                    <th>Abgeschlossen</th>
                    <th>Quote</th>
                    <th>∅ Dauer</th>
                  </tr></thead>
                  <tbody>{solverData.agents.map(a => {
                    // quote === null: abgeschlossen > gesamt, also keine
                    // bildbare Quote (siehe quoteOderNichts im Backend). Dann
                    // „–" statt eines Werts ueber 100 %, der nach einem Fehler
                    // aussieht — und ein neutrales Abzeichen, kein rotes.
                    const aQuoteClass = a.quote === null ? 'quote-badge--mid'
                      : a.quote >= 60 ? 'quote-badge--good' : a.quote >= 35 ? 'quote-badge--mid' : 'quote-badge--low';
                    return (
                      <tr
                        key={a.agentId}
                        className={isDrill(`solver:${a.agentName}`) ? 'is-selected' : ''}
                        onClick={() => toggleDrilldown(makeDrill({ key: `solver:${a.agentName}`, source: 'solver', type: 'solver', value: a.agentName, agentId: a.agentId, label: `Mitarbeiter: ${a.agentName}` }))}
                        title="Klick — Tickets dieses Mitarbeiters aufklappen"
                      >
                        <td className="col-name">{a.agentName}</td>
                        <td>{a.gesamt}</td>
                        <td className="col-solved">{a.abgeschlossen}</td>
                        <td>
                          <span
                            className={`quote-badge ${aQuoteClass}`}
                            title={a.quote === null
                              ? 'Keine Quote bildbar: mehr abgeschlossen als gesamt. Tickets dieses Mitarbeiters wurden nach dem Lösen umverteilt.'
                              : undefined}
                          >{a.quote === null ? '–' : `${a.quote}%`}</span>
                        </td>
                        <td>{a.bearbeitungAvgFormatiert}</td>
                      </tr>
                    );
                  })}</tbody>
                </table>
              </div>
            </div>
          ) : (
            <p className="col-muted">Keine Mitarbeiter-Daten für diese Abteilung im gewählten Zeitraum.</p>
          )}
          {renderDrilldowns('solver')}
        </CollapsibleSection>
      )}

      {/* ── FCR Rate monatlich ── */}
      {abteilungenData?.abteilungen?.length > 0 && (
        <CollapsibleSection
          {...blockProps('fcrMonatlich')}
          title="FCR Rate — Monatliche Übersicht"
          hint="First Contact Resolution pro Monat und Mitarbeiter — aus vorberechneten Wochendaten"
        >
          <div className="solver-abteilung-picker">
            <label htmlFor="fcr-gruppe-select">Abteilung</label>
            <select
              id="fcr-gruppe-select"
              value={fcrGruppeId}
              onChange={e => { setFcrGruppeId(e.target.value); setFcrSelectedMonth(''); }}
            >
              {abteilungenData.abteilungen.map(ab => (
                <option key={ab.gruppeId} value={ab.gruppeId}>{ab.gruppeName}</option>
              ))}
            </select>
          </div>

          {fcrData?.agents?.length > 0 ? (() => {
            const months = fcrData.months || [];
            const MONTH_NAMES = ['Jan','Feb','Mär','Apr','Mai','Jun','Jul','Aug','Sep','Okt','Nov','Dez'];
            const monthLabel = m => {
              const [yr, mo] = m.split('-');
              return `${MONTH_NAMES[parseInt(mo,10)-1]} ${yr}`;
            };
            const sel = fcrSelectedMonth || months[months.length - 1];
            const teamD = fcrData.teamTotals?.[sel];
            const fcrBadgeClass = r => r >= 60 ? 'quote-badge--good' : r >= 35 ? 'quote-badge--mid' : 'quote-badge--low';

            return (
              <div>
                {/* Monats-Pills */}
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 16 }}>
                  {months.map(m => {
                    const d = fcrData.teamTotals?.[m];
                    const isPending = d?.pending;
                    const isActive = m === sel;
                    return (
                      <button
                        key={m}
                        type="button"
                        onClick={() => setFcrSelectedMonth(m)}
                        style={{
                          padding: '4px 12px',
                          borderRadius: 20,
                          border: isActive ? '2px solid var(--accent)' : '1px solid var(--border)',
                          background: isActive ? 'var(--accent)' : 'var(--surface)',
                          color: isActive ? '#fff' : 'var(--text)',
                          cursor: 'pointer',
                          fontSize: 13,
                          fontWeight: isActive ? 600 : 400,
                          display: 'flex',
                          alignItems: 'center',
                          gap: 4,
                        }}
                      >
                        {monthLabel(m)}
                        {isPending && <span style={{ opacity: 0.7, fontSize: 11 }}>~</span>}
                      </button>
                    );
                  })}
                </div>

                {/* Team-Gesamt KPI-Karte */}
                {teamD && (
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 20,
                    padding: '12px 16px',
                    borderRadius: 8,
                    background: 'var(--surface)',
                    border: '1px solid var(--border)',
                    marginBottom: 16,
                  }}>
                    <div>
                      <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>Team-Gesamt FCR</div>
                      {teamD.pending ? (
                        <div style={{ fontSize: 22, fontWeight: 700, color: 'var(--text-muted)' }}>~</div>
                      ) : (
                        <div style={{ fontSize: 22, fontWeight: 700 }}>
                          <span className={`quote-badge ${fcrBadgeClass(teamD.fcrRate ?? 0)}`} style={{ fontSize: 18 }}>
                            {teamD.fcrRate ?? 0}%
                          </span>
                        </div>
                      )}
                    </div>
                    <div style={{ color: 'var(--text-muted)', fontSize: 13 }}>
                      {!teamD.pending && <>{teamD.fcrCount ?? 0} FCR-Tickets von {teamD.fcrTotal ?? 0} Basis</>}
                    </div>
                    {teamD.pending && (
                      <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>
                        Laufender Monat — FCR wird erst nach Monatsabschluss vollständig berechnet
                      </div>
                    )}
                  </div>
                )}

                {/* Mitarbeiter-Tabelle für gewählten Monat */}
                <table className="analysis-table">
                  <thead>
                    <tr>
                      <th>Mitarbeiter</th>
                      <th style={{ textAlign: 'center' }}>FCR-Tickets</th>
                      <th style={{ textAlign: 'center' }}>Basis</th>
                      <th style={{ textAlign: 'center' }}>FCR-Rate</th>
                      <th style={{ textAlign: 'center' }}>Hold-Dauer</th>
                    </tr>
                  </thead>
                  <tbody>
                    {fcrData.agents
                      .map(a => ({ ...a, d: a.months[sel] }))
                      .filter(a => a.d?.fcrTotal > 0)
                      .sort((a, b) => (b.d?.fcrRate ?? -1) - (a.d?.fcrRate ?? -1))
                      .map(a => {
                        const d = a.d;
                        return (
                          <tr key={a.agentId}>
                            <td className="col-name">{a.agentName}</td>
                            <td style={{ textAlign: 'center' }}>{d.pending ? '~' : (d.fcrCount ?? 0)}</td>
                            <td style={{ textAlign: 'center' }}>{d.pending ? '~' : (d.fcrTotal ?? 0)}</td>
                            <td style={{ textAlign: 'center' }}>
                              {d.pending
                                ? <span className="col-muted">~</span>
                                : d.fcrRate != null
                                  ? <span className={`quote-badge ${fcrBadgeClass(d.fcrRate)}`}>{d.fcrRate}%</span>
                                  : <span className="col-muted">–</span>
                              }
                            </td>
                            <td
                              style={{ textAlign: 'center', cursor: d.holdMinutes > 0 ? 'pointer' : undefined }}
                              className={d.holdMinutes > 0 && isDrill(`fcrHold:${a.agentName}:${sel}`) ? 'is-selected' : ''}
                              onClick={() => {
                                if (!d.holdMinutes) return;
                                toggleDrilldown(makeDrill({ key: `fcrHold:${a.agentName}:${sel}`, source: 'fcrHold', type: 'holdTickets', value: a.agentName, agentId: a.agentId, month: sel, label: `Hold-Tickets ${monthLabel(sel)}: ${a.agentName}` }));
                              }}
                              title={d.holdMinutes > 0 ? 'Klick — Tickets mit Hold-Zeit aufklappen' : undefined}
                            >
                              {d.holdMinutes != null ? formatDauer(d.holdMinutes) : <span className="col-muted">–</span>}
                            </td>
                          </tr>
                        );
                      })
                    }
                    {/* Team-Gesamt Zeile */}
                    {teamD && (
                      <tr style={{ fontWeight: 600, borderTop: '2px solid var(--border)' }}>
                        <td>Team gesamt</td>
                        <td style={{ textAlign: 'center' }}>{teamD.pending ? '~' : (teamD.fcrCount ?? 0)}</td>
                        <td style={{ textAlign: 'center' }}>{teamD.pending ? '~' : (teamD.fcrTotal ?? 0)}</td>
                        <td style={{ textAlign: 'center' }}>
                          {teamD.pending
                            ? <span className="col-muted">~</span>
                            : teamD.fcrRate != null
                              ? <span className={`quote-badge ${fcrBadgeClass(teamD.fcrRate)}`}>{teamD.fcrRate}%</span>
                              : <span className="col-muted">–</span>
                          }
                        </td>
                        <td style={{ textAlign: 'center' }}>
                          {teamD.holdMinutes != null ? formatDauer(teamD.holdMinutes) : <span className="col-muted">–</span>}
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
                <p className="col-muted" style={{ fontSize: 12, marginTop: 8 }}>
                  ~ = laufender Monat, FCR erst nach Monatsabschluss vollständig · Zielwert: 60%
                </p>
                {renderDrilldowns('fcrHold')}
              </div>
            );
          })() : (
            <p className="col-muted">Keine FCR-Daten für diese Abteilung.</p>
          )}
        </CollapsibleSection>
      )}

      {/* ── Monats-Trend (Eingangs-Kohorte pro Abteilung) ── */}
      {trendData?.months?.length > 0 && (
        <CollapsibleSection
          {...blockProps('trend')}
          title={`Monats-Trend ${trendData.year}`}
          hint="Pro Abteilung: neu eingegangene Tickets bzw. Ø Bearbeitung der abgeschlossenen — umschaltbar"
        >
          {/* Toggle: Anzahl Eingaenge vs Ø Bearbeitung + Excel-Export (GL) */}
          <div className="journey-filter-bar" style={{ marginBottom: 12, display: 'flex', alignItems: 'center' }}>
            <button
              type="button"
              className={`journey-filter-btn ${trendMode === 'anzahl' ? 'active' : ''}`}
              onClick={() => setTrendMode('anzahl')}
            >
              Anzahl Eingänge
            </button>
            <button
              type="button"
              className={`journey-filter-btn ${trendMode === 'bearbeitung' ? 'active' : ''}`}
              onClick={() => setTrendMode('bearbeitung')}
              title="Mittelwert — wird von wenigen sehr lange offenen Tickets getragen, siehe Hilfe"
            >
              Ø Bearbeitung
            </button>
            {/* Median und Langlieger kommen aus DENSELBEN Werten wie der Ø
                daneben — nur anders zusammengefasst. Der Ø ist bei dieser
                Verteilung kein Mass fuer den Normalfall: im April 2026 trugen
                1% der Tickets 74% der aufsummierten Minuten. */}
            <button
              type="button"
              className={`journey-filter-btn ${trendMode === 'median' ? 'active' : ''}`}
              onClick={() => setTrendMode('median')}
              title="Der typische Fall — die Hälfte der Tickets war schneller, die Hälfte langsamer"
            >
              Median Bearbeitung
            </button>
            <button
              type="button"
              className={`journey-filter-btn ${trendMode === 'langlieger' ? 'active' : ''}`}
              onClick={() => setTrendMode('langlieger')}
              title="Tickets, die länger als 24 Geschäftsstunden zugewiesen und offen waren"
            >
              Langlieger
            </button>
            <button
              type="button"
              className="journey-filter-btn"
              style={{ marginLeft: 'auto' }}
              onClick={handleExportTrendExcel}
              disabled={trendExporting}
              title="GL-Report: Diagramm + Tabellen als Excel exportieren"
            >
              {trendExporting ? '⏳ Export…' : '⬇ Excel-Export (GL)'}
            </button>
          </div>
          <div ref={trendChartRef}>
          <ResponsiveContainer width="100%" height={340}>
            <BarChart data={trendData.months} margin={{ top: 10, right: 10, left: -20, bottom: 0 }} barCategoryGap="20%" barGap={2}>
              <XAxis dataKey="monthLabel" tick={{ fontSize: 12 }} />
              <YAxis
                tick={{ fontSize: 11 }}
                tickFormatter={(trendMode === 'bearbeitung' || trendMode === 'median') ? formatDauerAchse : undefined}
              />
              <Tooltip
                formatter={(trendMode === 'bearbeitung' || trendMode === 'median')
                  ? (val, name) => [formatDauer(val), name]
                  : undefined}
              />
              <Legend wrapperStyle={{ fontSize: 11 }} />
              {/* EINE Spaltenliste fuer alle vier Modi (13.10.2026).
                  Vorher standen dieselben Balken zweimal da — einmal ohne und
                  einmal mit Namens-Suffix. Beim Ergaenzen von „Basis Module"
                  (A7) waeren es zwei Stellen gewesen, und eine vergessene
                  haette genau in einem Modus eine Spalte verschluckt, ohne
                  dass irgendetwas fehlschlaegt. */}
              {(() => {
                const suffix = trendMode === 'anzahl'      ? ''
                             : trendMode === 'bearbeitung' ? 'Avg'
                             : trendMode === 'median'      ? 'Median' : 'Lang';
                return TREND_SPALTEN.map(s => (
                  <Bar key={s.key} isAnimationActive={false}
                       dataKey={`${s.key}${suffix}`} name={s.name} fill={s.farbe} />
                ));
              })()}
            </BarChart>
          </ResponsiveContainer>
          </div>
        </CollapsibleSection>
      )}

      {/* ── Produkt-Top-10 ── */}
      {productData?.produkte?.length > 0 && (
        <CollapsibleSection {...blockProps('produkt')} title="Top 10 Produkte im Zeitraum" hint="Wo entstehen am meisten Tickets — Indikator fuer Schulung &amp; Dokumentation">
          <div className="analysis-grid-chart-table">
            <ResponsiveContainer width="100%" height={300}>
              <BarChart
                data={productData.produkte}
                layout="vertical"
                margin={{ top: 0, right: 10, left: 80, bottom: 0 }}
              >
                <XAxis type="number" tick={{ fontSize: 11 }} />
                <YAxis type="category" dataKey="produkt" tick={{ fontSize: 11 }} width={140} />
                <Tooltip />
                <Bar isAnimationActive={false} dataKey="anzahl" name="Tickets" fill="#3b82f6" />
              </BarChart>
            </ResponsiveContainer>
            <div style={{ overflowX: 'auto' }}>
              <table className="analysis-table">
                <thead><tr>
                  <th>Produkt</th>
                  <th>Tickets</th>
                  <th>Abgeschlossen</th>
                  <th>Quote</th>
                  <th>Forwards</th>
                  <th>∅ Dauer</th>
                </tr></thead>
                <tbody>{productData.produkte.map(p => {
                  const pQuoteClass = p.quote >= 60 ? 'quote-badge--good' : p.quote >= 35 ? 'quote-badge--mid' : 'quote-badge--low';
                  return (
                    <tr
                      key={p.produkt}
                      className={isDrill(`produkt:${p.produkt}`) ? 'is-selected' : ''}
                      onClick={() => toggleDrilldown(makeDrill({ key: `produkt:${p.produkt}`, source: 'produkt', type: 'produkt', value: p.produkt, label: `Produkt: ${p.produkt}` }))}
                      title="Klick — Tickets dieses Produkts aufklappen"
                    >
                      <td className="col-name">{p.produkt}</td>
                      <td>{p.anzahl}</td>
                      <td className="col-solved">{p.abgeschlossen}</td>
                      <td><span className={`quote-badge ${pQuoteClass}`}>{p.quote}%</span></td>
                      <td>{p.weiterleitungen} <span className="col-muted">({p.weiterleitungRate}%)</span></td>
                      <td>{p.bearbeitungAvgFormatiert}</td>
                    </tr>
                  );
                })}</tbody>
              </table>
            </div>
          </div>
          {renderDrilldowns('produkt')}
        </CollapsibleSection>
      )}

      {/* ── Forward-Ziele + Journey (zusammengeführt) ── */}
      {forwardData?.targets?.length > 0 && (
        <CollapsibleSection {...blockProps('forwardZiele')} title="Forward-Ziele" hint={`Wohin werden Tickets weitergeleitet — gesamt ${forwardData.totalForwards} Forwards`}>
          <div className="analysis-grid-chart-table">
            <ResponsiveContainer width="100%" height={260}>
              <BarChart
                data={forwardData.targets.slice(0, 10)}
                layout="vertical"
                margin={{ top: 0, right: 10, left: 80, bottom: 0 }}
              >
                <XAxis type="number" tick={{ fontSize: 11 }} />
                <YAxis type="category" dataKey="gruppeName" tick={{ fontSize: 11 }} width={140} />
                <Tooltip />
                <Bar isAnimationActive={false} dataKey="anzahl" name="Forwards" fill="#a855f7" />
              </BarChart>
            </ResponsiveContainer>
            <div style={{ overflowX: 'auto' }}>
              <table className="analysis-table">
                <thead><tr>
                  <th>Ziel-Gruppe</th>
                  <th>Forwards</th>
                  <th>Anteil</th>
                  <th>Davon Abgeschlossen</th>
                </tr></thead>
                <tbody>{forwardData.targets.map(t => {
                  const isSelected = journeySelectedTarget === t.gruppeId;
                  return (
                    <tr
                      key={t.gruppeId}
                      className={isSelected ? 'is-selected' : ''}
                      onClick={() => {
                        setJourneySelectedTarget(isSelected ? null : t.gruppeId);
                        setJourneyFilter('alle');
                        setJourneyExpanded(null);
                      }}
                      title="Klick — Tickets für dieses Ziel aufklappen"
                      style={{ cursor: 'pointer' }}
                    >
                      <td className="col-name">{t.gruppeName} {isSelected ? '▲' : '▼'}</td>
                      <td>{t.anzahl}</td>
                      <td><span className="quote-badge quote-badge--mid">{t.anteil}%</span></td>
                      <td className="col-solved">{t.abgeschlossen}</td>
                    </tr>
                  );
                })}</tbody>
              </table>
            </div>
          </div>

          {/* Journey-Ticket-Liste — erscheint wenn eine Zielgruppe gewählt */}
          {journeySelectedTarget && journeyData?.tickets && (() => {
            const targetName = forwardData.targets.find(t => t.gruppeId === journeySelectedTarget)?.gruppeName || journeySelectedTarget;
            const countAll      = journeyFiltered.length;
            const countUnsolved = journeyData.tickets.filter(t => String(t.forwardTarget?.id) === String(journeySelectedTarget) && t.isUnsolved).length;
            const countMultiHop = journeyData.tickets.filter(t => String(t.forwardTarget?.id) === String(journeySelectedTarget) && t.isMultiHop).length;
            return (
              <div style={{ marginTop: 16, borderTop: '2px solid var(--accent)', paddingTop: 12 }}>
                <div style={{ fontWeight: 600, marginBottom: 10, fontSize: 14 }}>
                  Tickets weitergeleitet an: {targetName}
                </div>
                <div className="journey-filter-bar" style={{ marginBottom: 12 }}>
                  <button type="button" className={`journey-filter-btn ${journeyFilter === 'alle' ? 'active' : ''}`} onClick={() => setJourneyFilter('alle')}>
                    Alle ({countAll})
                  </button>
                  <button type="button" className={`journey-filter-btn ${journeyFilter === 'unsolved' ? 'active' : ''}`} onClick={() => setJourneyFilter('unsolved')}>
                    Nur ungelöst ({countUnsolved})
                  </button>
                  <button type="button" className={`journey-filter-btn ${journeyFilter === 'multihop' ? 'active' : ''}`} onClick={() => setJourneyFilter('multihop')}>
                    Nur Mehr-Hops ({countMultiHop})
                  </button>
                  <button
                    type="button"
                    className="journey-filter-btn"
                    style={{ marginLeft: 'auto' }}
                    onClick={() => exportJourneyCsv(journeyFiltered, `${targetName}_${journeyFilter}`)}
                    disabled={journeyFiltered.length === 0}
                    title="Gefilterte Liste als CSV exportieren"
                  >
                    ⬇ Export CSV
                  </button>
                </div>
                {journeyFiltered.length === 0 ? (
                  <div className="tickets-empty">Keine Tickets für diesen Filter.</div>
                ) : (
                  <div style={{ overflowX: 'auto' }}>
                    <table className="analysis-table">
                      <thead><tr>
                        <th>ID</th>
                        <th>Betreff</th>
                        <th>→ Wohin</th>
                        <th>Aktuell</th>
                        <th>Status</th>
                        <th>Gelöst von</th>
                        <th>Bearbeitung</th>
                      </tr></thead>
                      <tbody>{journeyFiltered.slice(0, 50).map(t => (
                        <React.Fragment key={t.id}>
                          <tr>
                            <td>
                              {t.zendeskUrl
                                ? <a href={t.zendeskUrl} target="_blank" rel="noopener noreferrer" className="ticket-link">#{t.id}</a>
                                : <>#{t.id}</>}
                            </td>
                            <td className="td-subject" title={t.subject}>{t.subject}</td>
                            <td className="col-name">{t.forwardTarget?.name || '–'}</td>
                            <td>
                              {t.currentGroup?.name || '–'}
                              {t.isMultiHop && (
                                <span
                                  className={`hop-badge hop-badge--clickable ${journeyExpanded === t.id ? 'hop-badge--active' : ''}`}
                                  onClick={() => handleJourneyToggle(t.id)}
                                  title="Klick zeigt den Verlauf des Tickets"
                                >
                                  {journeyExpanded === t.id ? '▲ Verlauf' : '↻ Mehr-Hops'}
                                </span>
                              )}
                            </td>
                            <td>
                              <span className={`status-badge ${STATUS_COLORS[t.status] || ''}`}>
                                {STATUS_LABELS[t.status] || t.status}
                              </span>
                            </td>
                            <td>
                              {t.solvedByAgent
                                ? <span>{t.solvedByAgent.name}{t.solvedByAgent.group ? <span className="col-muted"> · {t.solvedByAgent.group.name}</span> : null}</span>
                                : <span className="col-muted">noch nicht geloest</span>}
                            </td>
                            <td className="col-muted">
                              {t.bearbeitungMin != null ? `${t.bearbeitungMin}m` : '–'}
                            </td>
                          </tr>
                          {journeyExpanded === t.id && (
                            <tr>
                              <td colSpan={7} style={{ padding: 0 }}>
                                <TicketTimeline
                                  ticket={{ created_at: t.createdAt, kanal: t.kanal || 'unbekannt' }}
                                  auditData={auditCache[t.id]}
                                  loading={!!auditLoading[t.id]}
                                />
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      ))}</tbody>
                    </table>
                    {journeyFiltered.length > 50 && (
                      <div style={{ padding: '12px 16px', fontSize: 12, color: 'var(--text-secondary, #666)', textAlign: 'center' }}>
                        ... und {journeyFiltered.length - 50} weitere Tickets — im CSV-Export alle {journeyFiltered.length} enthalten
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })()}

          {renderDrilldowns('forwardZiele')}
        </CollapsibleSection>
      )}

      {/* ── Re-Open-Quote ── */}
      {/* Sektion selbst haengt NICHT mehr an reopenData (Lazy-Load-Fix
          2026-07-21): reopenData ist null bis die Sektion aufgeklappt wird
          (enabled=reopenOpen) — haenge man die Sichtbarkeit der Sektion an
          reopenData, koennte sie NIE aufgeklappt werden (Henne-Ei-Problem).
          Gleiches Muster wie "Mitarbeiter nach Abteilung" (haengt an
          abteilungenData, nicht an solverData). */}
      {abteilungenData?.abteilungen?.length > 0 && (
        <CollapsibleSection
          {...blockProps('reopen')}
          title="Re-Open-Quote"
          hint={reopenData
            ? `Loeseversuche die spaeter wieder geoeffnet wurden — jeder Solver zaehlt fuer seinen eigenen Versuch, auch bei Uebergabe an einen anderen Mitarbeiter (${reopenData.gesamtReopened} von ${reopenData.gesamtSolved} Loeseversuchen). "hold" zaehlt nicht als Reopen. Klick auf eine Zeile zeigt die betroffenen Tickets (gelb=1x, rot=mehrfach wiedereroeffnet).`
            : 'Loeseversuche die spaeter wieder geoeffnet wurden — aufklappen zum Laden'}
        >
          <div className="solver-abteilung-picker">
            <label htmlFor="reopen-abteilung-select">Abteilung</label>
            <select
              id="reopen-abteilung-select"
              value={selectedAbteilungReopen}
              onChange={e => setSelectedAbteilungReopen(e.target.value)}
            >
              {abteilungenData.abteilungen.map(ab => (
                <option key={ab.gruppeId} value={ab.gruppeId}>{ab.gruppeName}</option>
              ))}
            </select>
          </div>

          {reopenLoading ? (
            <p className="col-muted" style={{ padding: '12px 16px' }}>Lädt…</p>
          ) : reopenData?.agents?.length > 0 && reopenData.gesamtReopened > 0 ? (
            <>
              <div style={{ overflowX: 'auto' }}>
                <table className="analysis-table">
                  <thead><tr>
                    <th>Mitarbeiter</th>
                    <th>Stabil geloest</th>
                    <th>Wieder offen</th>
                    <th>Re-Open-Rate</th>
                    <th>Betroffene Tickets</th>
                  </tr></thead>
                  <tbody>{reopenData.agents.slice(0, 10).map(a => {
                    // Niedrige Re-Open-Rate = gut (gruen), hohe = problematisch (rot)
                    const rClass = a.reopenRate <= 5 ? 'quote-badge--good' : a.reopenRate <= 15 ? 'quote-badge--mid' : 'quote-badge--low';
                    return (
                      <tr
                        key={a.agentId}
                        className={isDrill(`reopen:${a.agentName}`) ? 'is-selected' : ''}
                        onClick={() => toggleDrilldown(makeDrill({ key: `reopen:${a.agentName}`, source: 'reopen', type: 'reopenTickets', value: a.agentName, agentId: a.agentId, label: `Wieder geöffnete Tickets: ${a.agentName}` }))}
                        title="Klick — wieder geöffnete Tickets dieses Mitarbeiters aufklappen"
                      >
                        <td className="col-name">{a.agentName}</td>
                        <td className="col-solved">{a.solvedStabil}</td>
                        <td>{a.reopened}</td>
                        <td><span className={`quote-badge ${rClass}`}>{a.reopenRate}%</span></td>
                        <td>{a.reopenedTicketsTotal}</td>
                      </tr>
                    );
                  })}</tbody>
                </table>
              </div>
              {renderDrilldowns('reopen')}
            </>
          ) : (
            <p className="col-muted" style={{ padding: '12px 16px' }}>Keine wiedereröffneten Tickets im gewählten Zeitraum.</p>
          )}
        </CollapsibleSection>
      )}



      {/* ── KI-Antwortvorschläge ──
          Leitkennzahl ist bewusst Reopen und nicht das Bewertungsfeld: Reopen
          ist ein Ergebnis, keine Einschätzung — und es funktioniert auch dann
          noch, wenn der Bot irgendwann direkt an den Kunden antwortet und
          niemand mehr bewertet. */}
      {kiBotData?.ticketsGesamt > 0 && (
        <CollapsibleSection
          {...blockProps('kiBot')}
          title="KI-Antwortvorschläge"
          hint="Tickets mit Vorschlag des KI-Bots — Bewertung, Übernahme und ob das Ticket zurückkam"
        >
          <div style={{ padding: '10px 16px', fontSize: 12, color: 'var(--color-text-secondary)' }}>
            <strong>{kiBotData.ticketsGesamt}</strong> Tickets mit Vorschlag
            {' · '}Rechenzeit im Mittel <strong>{(kiBotData.dauerMedianMs / 1000).toFixed(1)} s</strong>
            {kiBotData.oeffentlich > 0 && <> {' · '}<strong>{kiBotData.oeffentlich}</strong> direkt an Kunden</>}
            {kiBotData.ausserhalb > 0 && (
              <div style={{ marginTop: 4, fontSize: 11 }}>
                Zahlen beziehen sich auf dieselben Tickets wie der Rest dieser Seite.
              </div>
            )}
          </div>

          {/* Wochenverlauf zuoberst — hier wird die Lernkurve sichtbar, und das
              ist die Frage, um die es langfristig geht. Die laufende Woche ist
              markiert, weil ihre Reopen-Quote systematisch zu gut aussieht (die
              3-Arbeitstage-Frist ist noch nicht um). */}
          {kiBotData.wochen?.length > 0 && (
            <div style={{ overflowX: 'auto' }}>
              <table className="analysis-table analysis-table--kompakt">
                <thead><tr>
                  <th>Woche</th>
                  <th>Tickets</th>
                  <th>passende Lösung</th>
                  <th>falscher Vorschlag</th>
                  <th>KI nicht relevant</th>
                  <th>Wieder aufgegangen</th>
                </tr></thead>
                <tbody>{kiBotData.wochen.map(w => (
                  <tr key={w.woche} className="ist-statisch">
                    <td>
                      {w.woche}
                      {!w.final && <span className="col-muted" title="Die 3-Arbeitstage-Frist für Wiedereröffnungen läuft noch — die Quote ist noch nicht endgültig"> (läuft noch)</span>}
                    </td>
                    <td>{w.tickets}</td>
                    <td>{w.bewertungsAnteile?.['passende Lösung'] ?? 0}</td>
                    <td>{w.bewertungsAnteile?.['falscher Vorschlag'] ?? 0}</td>
                    <td>{w.bewertungsAnteile?.['KI nicht relevant'] ?? 0}</td>
                    <td>
                      {w.reopenQuote !== null ? `${Math.round(w.reopenQuote * 100)}%` : <span className="col-muted">–</span>}
                      {!w.final && <span className="col-muted"> *</span>}
                    </td>
                  </tr>
                ))}</tbody>
              </table>
            </div>
          )}

          {/* EINE Tabelle für die Bewertung, mit beiden Arbeitsanweisungen als
              eigene Spalten. Vorher standen hier drei Tabellen; zwei davon
              zeigten dieselben Tickets noch einmal:
              — „Antwort übernommen?" (1:1 gegen überarbeitet) war in Summe
                exakt die Zeile „passende Lösung". Und der Split trennte gar
                nicht Übernahme von Überarbeitung: `mailantwort_ki` ist keiner
                der drei Tagger-Tags des Felds, kommt also NUR aus dem Makro —
                gemessen wurde das Werkzeug, nicht die Antwort.
              — die eigene KI-Training-Tabelle war Tickets minus Spaltenwert.
              Beides steckt jetzt in den Spalten „davon per Makro" und „davon
              mit KI-Training", beide aufklappbar. */}
          <div style={{ overflowX: 'auto', marginTop: 14 }}>
            <table className="analysis-table analysis-table--kompakt">
              <thead><tr>
                <th>Bewertung durch den Agenten</th>
                <th>Tickets</th>
                <th title={'Stichwort mailantwort_ki — wird nur vom Makro „Textvorlage::Mailantwort (KI)" gesetzt, nicht beim manuellen Kopieren der Bot-Antwort'}>davon per Makro</th>
                <th title={'Stichwort ki_training — der Rückkanal zum Bot. Bei „falscher Vorschlag" sind die Agenten angewiesen, das Trainings-Makro auszuführen.'}>davon mit KI-Training</th>
                <th>Gelöst</th>
                <th>Wieder aufgegangen</th>
                {/* Weiterleitung ist wie Reopen ein Ergebnis und keine
                    Einschätzung — und trennt die Bewertungen derzeit deutlich
                    schärfer. Stationen kommen aus computeEingangsEvents, also
                    aus derselben Funktion wie der Team-Vergleich: Wanderungen
                    innerhalb der First-Familie zählen hier folgerichtig NICHT
                    als Weiterleitung. */}
                <th title="Tickets, die ihre Abteilung nie verlassen haben. Stationen nach derselben Regel wie im Team-Vergleich — First-intern zählt nicht als Weiterleitung.">ohne Weiterleitung</th>
                <th title="Durchschnittliche Zahl der Abteilungen, die das Ticket durchlaufen hat. 1 = geblieben.">Ø Stationen</th>
                <th>FCR-Rate</th>
              </tr></thead>
              <tbody>{Object.entries(kiBotData.nachBewertung || {}).map(([name, z]) => (
                <tr key={name}
                  className={isDrill(`kiBot:bewertung:${name}`) ? 'is-selected' : ''}
                  onClick={() => toggleDrilldown(makeDrill({ key: `kiBot:bewertung:${name}`, source: 'kiBot', type: 'kiBot', value: `bewertung:${name}`, label: `KI-Bewertung: ${name}` }))}
                >
                  <td>{name}</td>
                  <td>{z.tickets}</td>
                  {/* Zellen-Drilldowns: stopPropagation, damit nicht zusätzlich
                      der Zeilen-Drilldown aufgeht. */}
                  <KiZelle
                    wert={z.uebernommen}
                    aktiv={isDrill(`kiBot:makro:${name}`)}
                    onKlick={() => toggleDrilldown(makeDrill({ key: `kiBot:makro:${name}`, source: 'kiBot', type: 'kiBot', value: `makro:${name}`, label: `Per Makro beantwortet — ${name}` }))}
                  />
                  {/* Bei „falscher Vorschlag" ist die Gegenzahl die eigentlich
                      interessante: dort ging dem Bot eine Korrektur verloren.
                      Deshalb dort zusätzlich „(N ohne)", ebenfalls anklickbar. */}
                  <td
                    onClick={(e) => { e.stopPropagation(); if (z.mitTraining > 0) toggleDrilldown(makeDrill({ key: `kiBot:training:${name}`, source: 'kiBot', type: 'kiBot', value: `training:${name}`, label: `Mit KI-Training — ${name}` })); }}
                    style={z.mitTraining > 0 ? { cursor: 'pointer' } : undefined}
                    className={isDrill(`kiBot:training:${name}`) ? 'is-selected' : ''}
                  >
                    {z.mitTraining > 0 ? z.mitTraining : <span className="col-muted">–</span>}
                    {name === 'falscher Vorschlag' && z.tickets - z.mitTraining > 0 && (
                      <span
                        className="col-muted"
                        style={{ cursor: 'pointer' }}
                        onClick={(e) => { e.stopPropagation(); toggleDrilldown(makeDrill({ key: `kiBot:ohneTraining:${name}`, source: 'kiBot', type: 'kiBot', value: `ohneTraining:${name}`, label: `Falscher Vorschlag ohne KI-Training` })); }}
                      > ({z.tickets - z.mitTraining} ohne)</span>
                    )}
                  </td>
                  <td>{z.geloest}</td>
                  <td
                    onClick={(e) => { e.stopPropagation(); if (z.reopened > 0) toggleDrilldown(makeDrill({ key: `kiBot:bewertung:${name}:reopened`, source: 'kiBot', type: 'kiBot', value: `bewertung:${name}`, nurReopened: true, label: `Wieder aufgegangen — ${name}` })); }}
                    style={z.reopened > 0 ? { cursor: 'pointer' } : undefined}
                    className={isDrill(`kiBot:bewertung:${name}:reopened`) ? 'is-selected' : ''}
                  >
                    {z.reopenQuote !== null ? `${Math.round(z.reopenQuote * 100)}%` : <span className="col-muted">–</span>}
                    <span className="col-muted"> ({z.reopened})</span>
                  </td>
                  <td
                    onClick={(e) => { e.stopPropagation(); if (z.direkt > 0) toggleDrilldown(makeDrill({ key: `kiBot:direkt:${name}`, source: 'kiBot', type: 'kiBot', value: `direkt:${name}`, label: `Ohne Weiterleitung — ${name}` })); }}
                    style={z.direkt > 0 ? { cursor: 'pointer' } : undefined}
                    className={isDrill(`kiBot:direkt:${name}`) ? 'is-selected' : ''}
                  >
                    {z.direktQuote !== null ? `${Math.round(z.direktQuote * 100)}%` : <span className="col-muted">–</span>}
                    <span className="col-muted"> ({z.direkt})</span>
                  </td>
                  <td>{z.stationenAvg !== null ? z.stationenAvg.toFixed(2) : <span className="col-muted">–</span>}</td>
                  <td>{z.fcrRate !== null ? `${Math.round(z.fcrRate * 100)}%` : <span className="col-muted">–</span>}</td>
                </tr>
              ))}</tbody>
            </table>
            {/* Statt einer stillen Null: gibt es „falscher Vorschlag"-Tickets,
                aber gar kein erkanntes Training, ist das fast sicher ein Defekt
                der Erkennung (Stichwort im Makro geändert oder Makro-Text
                angepasst) — und nicht „niemand nutzt den Rückkanal". Genau
                diese Verwechslung soll die Auswertung nicht produzieren. */}
            {kiBotData.trainingVerdaechtig && (
              <div style={{ fontSize: 11, color: '#b91c1c', marginTop: 6 }}>
                Kein einziges KI-Training erkannt, obwohl es „falscher Vorschlag"-Tickets
                gibt — vermutlich stimmt das Stichwort im Makro oder der erwartete
                Kommentartext nicht mehr.
              </div>
            )}
          </div>

          {/* Begründung „KI nicht relevant" — Erfassungsquote.
              „KI nicht relevant" ist der grösste und zugleich blindeste Eimer
              der Auswertung; der Pflicht-Freitext seit 17.09. ist das einzige,
              was ihn aufschlüsseln kann. Diese Zeile misst nicht den Inhalt,
              sondern ob das Feld überhaupt bedient wird — ohne das wäre jede
              spätere Aussage über die Gründe auf Sand gebaut. */}
          {kiBotData.begruendung?.tickets > 0 && (
            <div style={{ overflowX: 'auto', marginTop: 14 }}>
              <table className="analysis-table analysis-table--kompakt">
                <thead><tr>
                  <th>Begründung <span className="col-muted">(„KI nicht relevant")</span></th>
                  <th>Tickets</th>
                  <th>Anteil</th>
                </tr></thead>
                <tbody>
                  <tr
                    className={isDrill('kiBot:begrErfasst') ? 'is-selected' : ''}
                    onClick={() => toggleDrilldown(makeDrill({ key: 'kiBot:begrErfasst', source: 'kiBot', type: 'kiBot', value: 'begruendung:erfasst', label: 'Begründung erfasst' }))}
                  >
                    <td>erfasst</td>
                    <td>{kiBotData.begruendung.erfasst}</td>
                    <td>
                      {kiBotData.begruendung.quote !== null && (
                        <span className={`quote-badge ${kiBotData.begruendung.quote >= 0.9 ? 'quote-badge--good'
                          : kiBotData.begruendung.quote >= 0.6 ? 'quote-badge--mid' : 'quote-badge--low'}`}>
                          {Math.round(kiBotData.begruendung.quote * 100)}%
                        </span>
                      )}
                    </td>
                  </tr>
                  {/* Ausfüllen und Aussagen sind zweierlei — ein Pflicht-
                      Freitextfeld erzeugt zwangsläufig Ausweichzeichen. Ohne
                      diese Zeile liest man die Zahl darüber zu gut. */}
                  {kiBotData.begruendung.ohneInhalt > 0 && (
                    <tr
                      className={isDrill('kiBot:begrOhneInhalt') ? 'is-selected' : ''}
                      onClick={() => toggleDrilldown(makeDrill({ key: 'kiBot:begrOhneInhalt', source: 'kiBot', type: 'kiBot', value: 'begruendung:ohneInhalt', label: 'Begründung ohne erkennbaren Inhalt' }))}
                    >
                      <td style={{ paddingLeft: 26 }} className="col-muted">davon ohne erkennbaren Inhalt</td>
                      <td className="col-muted">{kiBotData.begruendung.ohneInhalt}</td>
                      <td></td>
                    </tr>
                  )}
                  <tr
                    className={isDrill('kiBot:begrFehlt') ? 'is-selected' : ''}
                    onClick={() => toggleDrilldown(makeDrill({ key: 'kiBot:begrFehlt', source: 'kiBot', type: 'kiBot', value: 'begruendung:fehlt', label: 'Ohne Begründung' }))}
                  >
                    <td>nicht erfasst</td>
                    <td>{kiBotData.begruendung.fehlt}</td>
                    <td></td>
                  </tr>
                </tbody>
              </table>
              {kiBotData.begruendung.abVorZeitraumStart && (
                <div className="col-muted" style={{ fontSize: 11, marginTop: 6 }}>
                  Nur Tickets ab {new Date(kiBotData.begruendung.ab).toLocaleDateString('de-CH')} —
                  vorher gab es das Feld in Zendesk noch nicht.
                </div>
              )}
            </div>
          )}

          {/* Bot-Tickets ausserhalb der Auswertungsmenge — eigene, aufklappbare
              Zeile statt nur einer Zahl im Kopf. Der Bot arbeitet breiter als
              das, was diese Seite auswertet; wer wissen will, was da liegt,
              soll es sehen koennen.
              BEWUSST nicht "nicht in den Support-Gruppen": gefiltert wird gegen
              die Auswertungsmenge der Seite, und die entsteht ueber die AGENTEN
              (fetchAllTicketsInRange). Ein frisch eingegangenes, noch nicht
              zugewiesenes Ticket gehoert damit kurzzeitig niemandem und landet
              hier — obwohl es sehr wohl in einer Support-Gruppe liegt.
              Gemessen 17.09.2026: von 565 Zeilen waren 509 genau solche Faelle
              (lokal verstaerkt, weil dort nur die Assignee-Quelle zieht). */}
          {kiBotData.ausserhalb > 0 && (
            <div style={{ overflowX: 'auto', marginTop: 14 }}>
              <table className="analysis-table analysis-table--kompakt">
                <thead><tr>
                  <th>Ausserhalb dieser Auswertung <span className="col-muted">(nicht in der Auswertungsmenge dieser Seite)</span></th>
                  <th>Tickets</th>
                </tr></thead>
                <tbody>
                  <tr
                    className={isDrill('kiBot:ausserhalb') ? 'is-selected' : ''}
                    onClick={() => toggleDrilldown(makeDrill({ key: 'kiBot:ausserhalb', source: 'kiBot', type: 'kiBot', value: 'ausserhalb', label: 'KI-Tickets ausserhalb der Auswertungsmenge' }))}
                  >
                    <td>Bot-Tickets ausserhalb der Auswertungsmenge</td>
                    <td>{kiBotData.ausserhalb}</td>
                  </tr>
                </tbody>
              </table>
              <div className="col-muted" style={{ fontSize: 11, marginTop: 6 }}>
                Enthält andere Gruppen (Aussendienst, ASP-IT …) und vorübergehend
                auch neu eingegangene Tickets, die noch keinem Agenten zugewiesen
                sind — diese rutschen mit der Zuweisung von selbst in die
                Auswertung.
              </div>
            </div>
          )}

          {/* Produkt-Landkarte. Das Ziel ist nicht „ist der Bot gut genug",
              sondern „für WELCHE Themen ist er gut genug" — die Freigabe zum
              Direktversand wird themenweise fallen, nicht global. Die
              Artikel-Heatmap darunter ist dafür eine Ebene zu fein (knapp 200
              Artikel gegen gut ein Dutzend Produkte).
              Sortiert nach Menge, NICHT nach Trefferquote: eine 100%-Quote aus
              zwei Tickets gehört nicht an die Spitze einer Liste, aus der
              später Freigabe-Entscheide abgeleitet werden. */}
          {kiBotData.produkte?.length > 0 && (
            <div style={{ overflowX: 'auto', marginTop: 14 }}>
              <table className="analysis-table analysis-table--kompakt">
                <thead><tr>
                  <th>Produkt</th>
                  <th>Tickets</th>
                  <th>passende Lösung</th>
                  <th>ohne Weiterleitung</th>
                  <th>Wieder aufgegangen</th>
                </tr></thead>
                <tbody>
                  {kiBotData.produkte.slice(0, 15).map(p => (
                    <tr key={p.produkt}
                      className={isDrill(`kiBot:produkt:${p.produkt}`) ? 'is-selected' : ''}
                      onClick={() => toggleDrilldown(makeDrill({ key: `kiBot:produkt:${p.produkt}`, source: 'kiBot', type: 'kiBot', value: `produkt:${p.produkt}`, label: `KI-Tickets — ${p.produkt}` }))}
                    >
                      <td>{p.produkt}</td>
                      <td>{p.tickets}</td>
                      <td>
                        {p.tickets > 0 ? `${Math.round(p.passend / p.tickets * 100)}%` : '–'}
                        <span className="col-muted"> ({p.passend})</span>
                      </td>
                      <td>{p.direktQuote !== null ? `${Math.round(p.direktQuote * 100)}%` : <span className="col-muted">–</span>}</td>
                      <td>
                        {p.reopenQuote !== null ? `${Math.round(p.reopenQuote * 100)}%` : <span className="col-muted">–</span>}
                        <span className="col-muted"> ({p.reopened})</span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <div className="col-muted" style={{ fontSize: 11, marginTop: 6 }}>
                {kiBotData.produkte.length} Produkte insgesamt. Bei kleinen Mengen
                sind die Quoten noch nicht belastbar — zwei Tickets ergeben 0 % oder 50 %.
              </div>
            </div>
          )}

          {renderDrilldowns('kiBot')}
        </CollapsibleSection>
      )}

      {/* ── Inbox-Warteschlange (Live) ── */}
      <CollapsibleSection
        {...blockProps('inbox')}
        title="Inbox-Warteschlange"
        hint="Aktuelle offene/neue/wartende Tickets einer Inbox — live aus Zendesk (2-Min-Cache)"
      >
        <InboxBlock />
      </CollapsibleSection>

      {/* ── Abgleich gegen Zendesk ── */}
      <CollapsibleSection
        {...blockProps('abgleich')}
        title="Abgleich gegen Zendesk"
        hint="Fehlt uns etwas? Gegenüberstellung von Zendesk und dieser Auswertung"
      >
        <AbgleichBlock fromDate={fromDate} toDate={toDate} />
      </CollapsibleSection>

      </div>{/* /analysis-blocks */}
    </div>
  );
}
